import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  Bell,
  CalendarDays,
  Check,
  CheckCircle2,
  Download,
  Image as ImageIcon,
  Lock,
  MapPin,
  MessageSquareText,
  PackageCheck,
  Ruler,
  Share2,
  ShoppingBag,
  Sparkles,
  Trash2,
  Upload,
  UserRound,
  WalletCards,
  X,
} from "lucide-react";
import AccountLayout, { ACCOUNT_NAV_GROUPS } from "../components/account/AccountLayout";
import { AppointmentModal } from "../components/atelier/AppointmentModal";
import { PillCTA as AtelierCTA } from "../components/atelier/primitives";
import { productImage, ss26Image } from "../components/storefront/assets";
import AdfImage from "../components/storefront/AdfImage";
import OrderInvoice from "../components/account/OrderInvoice";
import { api, API_BASE, mediaUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { formatAZN, pickLocale } from "../lib/format";
import { useI18n } from "../lib/i18n";
import defaultAiAvatar from "../assets/account/ai-avatar-default.jpg";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { analytics } from "../lib/analytics";

const ALL_TABS = ACCOUNT_NAV_GROUPS.flatMap((group) => group.items.map((item) => item.key));
const ACCOUNT_DATA = {
  orders: [],
  profile: null,
  addresses: [],
  loyalty: null,
  ledger: [],
  vip: null,
  vouchers: [],
};

const PROFILE_DEFAULTS = {
  height_cm: "",
  weight_kg: "",
  body_type: "",
  jacket_size: "",
  trouser_size: "",
  shirt_collar_size: "",
  shoe_size: "",
  preferred_fit: "",
  favorite_colors: [],
  style_tags: [],
};

const ADDRESS_DEFAULTS = {
  label: "Ev",
  first_name: "",
  last_name: "",
  phone: "",
  country: "AZ",
  city: "",
  line1: "",
  line2: "",
  postal_code: "",
  is_default_shipping: false,
  is_default_billing: false,
};

const AVATAR_REQUIREMENT_KEYS = [
  "account.style.requirement.full_body",
  "account.style.requirement.face",
  "account.style.requirement.pose",
  "account.style.requirement.light",
  "account.style.requirement.clothing",
];
const AZ_PHONE_PREFIX = "+994";

function PillCTA({ className = "", style, ...props }) {
  return (
    <AtelierCTA
      {...props}
      style={{ textTransform: "none", letterSpacing: 0, borderRadius: 0, ...(style || {}) }}
      className={`min-h-11 rounded-none px-5 text-sm normal-case tracking-normal disabled:pointer-events-none disabled:opacity-45 ${className}`}
    />
  );
}

function SmallCaps({ children, className = "", tone = "brass", ...rest }) {
  const toneClass = tone === "ivory" ? "text-ivory/70" : tone === "graphite" ? "text-graphite/70" : "text-espresso";
  return (
    <span className={`inline-block text-xs font-medium ${toneClass} ${className}`} {...rest}>
      {children}
    </span>
  );
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

async function apiErrorDetail(error, fallback) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed?.detail || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.detail || fallback;
}

function fullName(user) {
  return [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
}

function initialsFor(user) {
  const first = user?.first_name?.trim()?.[0] || "";
  const last = user?.last_name?.trim()?.[0] || "";
  return `${first}${last}`.toUpperCase() || "ADF";
}

function asDate(value, locale = "az") {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(locale === "az" ? "az-AZ" : "en-US", { dateStyle: "medium" });
  } catch {
    return "";
  }
}

function asDateTime(value, locale = "az") {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(locale === "az" ? "az-AZ" : "en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
}

function localPhoneDigits(value) {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.startsWith("994")) digits = digits.slice(3);
  if (digits.startsWith("0")) digits = digits.slice(1);
  return digits.slice(0, 9);
}

function formatLocalPhone(value) {
  const digits = localPhoneDigits(value);
  const parts = [];
  if (digits.slice(0, 2)) parts.push(`(${digits.slice(0, 2)}`);
  if (digits.length >= 2) parts[0] = `(${digits.slice(0, 2)})`;
  if (digits.slice(2, 5)) parts.push(digits.slice(2, 5));
  if (digits.slice(5, 7)) parts.push(digits.slice(5, 7));
  if (digits.slice(7, 9)) parts.push(digits.slice(7, 9));
  return parts.join(" ");
}

function e164Phone(value) {
  const digits = localPhoneDigits(value);
  return digits.length === 9 ? `${AZ_PHONE_PREFIX}${digits}` : "";
}

function displayAzPhone(value) {
  return `${AZ_PHONE_PREFIX} ${formatLocalPhone(value)}`.trim();
}

function onlyOtpDigits(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 6);
}

function profileCompletion({ user, profile, addresses }) {
  const fields = [
    user?.first_name,
    user?.last_name,
    user?.phone,
    user?.email,
    user?.birth_date,
    profile?.height_cm,
    profile?.weight_kg,
    profile?.preferred_fit,
    profile?.jacket_size,
    safeArray(addresses).length > 0,
  ];
  const done = fields.filter(Boolean).length;
  return Math.round((done / fields.length) * 100);
}

function nextAppointment(rows = []) {
  const now = Date.now();
  return safeArray(rows)
    .filter((item) => !["cancelled", "completed"].includes(item.status) && new Date(item.slot_start).getTime() >= now)
    .sort((a, b) => new Date(a.slot_start) - new Date(b.slot_start))[0] || null;
}

function compactProductTitle(product, locale) {
  return pickLocale(product?.title, locale) || product?.name || product?.handle || "";
}

function sectionTitle(t, key) {
  return t(`account.wardrobe.section.${key}`);
}

function orderStatusTone(status) {
  if (["delivered", "completed"].includes(status)) return "border-emerald-900/15 bg-emerald-50 text-emerald-950";
  if (["cancelled", "returned", "failed"].includes(status)) return "border-red-900/15 bg-red-50 text-red-900";
  if (["shipped", "confirmed", "prepared", "preparing"].includes(status)) return "border-espresso/30 bg-stone/35 text-graphite";
  return "border-graphite/10 bg-paper text-graphite/70";
}

function labelFor(t, namespace, value) {
  if (!value) return t("account.common.not_set");
  const key = `account.${namespace}.${String(value).toLowerCase()}`;
  const translated = t(key);
  return translated === key ? value : translated;
}

function normalizeProfile(profile) {
  return {
    ...PROFILE_DEFAULTS,
    ...(profile || {}),
    favorite_colors: safeArray(profile?.favorite_colors),
    style_tags: safeArray(profile?.style_tags),
  };
}

function useBeforeUnload(active) {
  useEffect(() => {
    if (!active) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);
}

function IconBadge({ icon: Icon = Sparkles }) {
  return (
    <span className="inline-flex h-10 w-10 items-center justify-center border border-espresso/35 bg-stone/25 text-espresso" aria-hidden="true">
      <Icon size={18} strokeWidth={1.5} />
    </span>
  );
}

function PageHeader({ eyebrowKey, titleKey, bodyKey, action = null }) {
  const { t } = useI18n();
  useDocumentTitle(t("account.doc_title", "Hesabım"));
  return (
    <header className="mb-8 flex flex-col gap-5 border-b border-graphite/10 pb-6 md:flex-row md:items-end md:justify-between">
      <div className="max-w-3xl">
        {eyebrowKey && <SmallCaps>{t(eyebrowKey)}</SmallCaps>}
        <h1 className="mt-2 font-serif text-[clamp(34px,4vw,52px)] leading-none text-graphite">{t(titleKey)}</h1>
        {bodyKey && <p className="mt-3 max-w-2xl text-[15px] leading-7 text-graphite/75">{t(bodyKey)}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
  );
}

function EmptyState({ icon = Sparkles, titleKey, bodyKey, action = null, secondary = null }) {
  const { t } = useI18n();
  const Icon = icon;
  return (
    <div className="border border-graphite/10 bg-paper p-6 sm:p-7" data-testid="account-empty-state">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <IconBadge icon={Icon} />
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-[26px] leading-tight text-graphite">{t(titleKey)}</h3>
          <p className="mt-2 max-w-xl text-sm leading-6 text-graphite/75">{t(bodyKey)}</p>
          {(action || secondary) && (
            <div className="mt-5 flex flex-wrap gap-3">
              {action}
              {secondary}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InlineNotice({ tone = "success", children }) {
  const classes = tone === "danger"
    ? "border-red-900/15 bg-red-50 text-red-900"
    : tone === "warning"
      ? "border-espresso/35 bg-stone/35 text-graphite"
      : "border-emerald-900/15 bg-emerald-50 text-emerald-950";
  return <div className={`border p-3 text-sm leading-6 ${classes}`} role="status">{children}</div>;
}

function StatusPill({ children, status }) {
  return (
    <span className={`inline-flex min-h-8 items-center border px-3 text-xs ${orderStatusTone(status)}`}>
      {children}
    </span>
  );
}

function TextInput({ labelKey, hintKey, value, onChange, error, type = "text", inputMode, autoComplete, maxLength, required = false, readOnly = false, testid }) {
  const { t } = useI18n();
  return (
    <label className="block">
      <span className="flex items-center justify-between gap-3 text-sm font-medium text-graphite">
        {t(labelKey)}
        {!required && <span className="text-xs font-normal text-graphite/70">{t("account.common.optional")}</span>}
      </span>
      <input
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        value={value ?? ""}
        readOnly={readOnly}
        placeholder={readOnly ? undefined : t(labelKey)}
        onChange={(event) => onChange?.(event.target.value)}
        data-testid={testid}
        className={`mt-2 min-h-11 w-full border bg-white px-3 text-sm outline-none transition focus:border-espresso ${
          readOnly ? "border-graphite/10 text-graphite/70" : "border-graphite/18 text-graphite"
        }`}
        aria-invalid={Boolean(error)}
      />
      {hintKey && <span className="mt-1 block text-xs leading-5 text-graphite/70">{t(hintKey)}</span>}
      {error && <span className="mt-1 block text-xs text-red-900">{error}</span>}
    </label>
  );
}

function SelectInput({ labelKey, value, onChange, options, hintKey, required = false, testid }) {
  const { t } = useI18n();
  return (
    <label className="block">
      <span className="flex items-center justify-between gap-3 text-sm font-medium text-graphite">
        {t(labelKey)}
        {!required && <span className="text-xs font-normal text-graphite/70">{t("account.common.optional")}</span>}
      </span>
      <select
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testid}
        className="mt-2 min-h-11 w-full border border-graphite/18 bg-white px-3 text-sm text-graphite outline-none transition focus:border-espresso"
      >
        <option value="">{t("account.common.choose")}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
        ))}
      </select>
      {hintKey && <span className="mt-1 block text-xs leading-5 text-graphite/70">{t(hintKey)}</span>}
    </label>
  );
}

function TextAreaInput({ labelKey, value, onChange, rows = 4, testid }) {
  const { t } = useI18n();
  return (
    <label className="block">
      <span className="text-sm font-medium text-graphite">{t(labelKey)}</span>
      <textarea
        rows={rows}
        value={value ?? ""}
        placeholder={t(labelKey)}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testid}
        className="mt-2 w-full resize-none border border-graphite/18 bg-white px-3 py-3 text-sm text-graphite outline-none transition focus:border-espresso"
      />
    </label>
  );
}

function SegmentGroup({ labelKey, value, onChange, options }) {
  const { t } = useI18n();
  return (
    <div>
      <div className="mb-2 text-sm font-medium text-graphite">{t(labelKey)}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`min-h-11 border px-4 text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso ${
              value === option.value ? "border-graphite bg-graphite text-ivory" : "border-graphite/18 bg-white text-graphite/75 hover:border-espresso"
            }`}
          >
            {t(option.labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
}

function SidePanel({ open, titleKey, onClose, children, width = "max-w-xl" }) {
  const { t } = useI18n();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-graphite/35" role="dialog" aria-modal="true">
      <div className={`ml-auto flex h-full w-full ${width} flex-col bg-ivory shadow-sm`}>
        <div className="flex min-h-16 items-center justify-between border-b border-graphite/10 px-5">
          <h2 className="font-serif text-2xl text-graphite">{t(titleKey)}</h2>
          <button type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso" aria-label={t("common.close")}>
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

function SkeletonRows({ count = 4 }) {
  return (
    <div className="space-y-3" aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="h-20 animate-pulse bg-paper" />
      ))}
    </div>
  );
}

class AccountTabBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    console.error(`[atelier] account tab '${this.props.tabKey}' failed`, error, info);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="border border-red-900/20 bg-red-50 p-6" data-testid="account-tab-error">
          <SmallCaps>{this.props.t("account.error.section_title")}</SmallCaps>
          <p className="mt-2 text-sm leading-6 text-red-900">{this.props.t("account.error.section_body")}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function Account() {
  const { user, ready, logout, reload } = useAuth();
  const [sp, setSp] = useSearchParams();
  const requestedTab = sp.get("tab") || "overview";
  const [tab, setTab] = useState(ALL_TABS.includes(requestedTab) ? requestedTab : "overview");
  const [data, setData] = useState(ACCOUNT_DATA);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  useEffect(() => {
    const next = sp.get("tab") || "overview";
    if (ALL_TABS.includes(next) && next !== tab) setTab(next);
    if (!ALL_TABS.includes(next)) {
      setTab("overview");
      setSp({ tab: "overview" }, { replace: true });
    }
  }, [sp, tab, setSp]);

  const loadAccountData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setLoadError("");
    const requests = {
      orders: api.get("/me/orders"),
      profile: api.get("/me/profile"),
      addresses: api.get("/me/addresses"),
      loyalty: api.get("/me/loyalty"),
      ledger: api.get("/me/loyalty/ledger"),
      vip: api.get("/me/vip"),
      vouchers: api.get("/me/vouchers"),
    };
    const entries = await Promise.allSettled(Object.entries(requests).map(async ([key, request]) => [key, (await request).data]));
    const next = { ...ACCOUNT_DATA };
    let failed = false;
    entries.forEach((entry) => {
      if (entry.status === "fulfilled") {
        next[entry.value[0]] = entry.value[1];
      } else {
        failed = true;
      }
    });
    setData(next);
    setLoadError(failed ? t("account.error.partial") : "");
    setLoading(false);
  }, [t, user]);

  useEffect(() => { loadAccountData(); }, [loadAccountData]);

  const navigateTab = (next) => {
    setTab(next);
    setSp({ tab: next }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmLogout = async () => {
    if (!window.confirm(t("account.logout.confirm"))) return;
    await logout();
    navigate("/");
  };

  if (!ready) return <div className="min-h-screen bg-ivory" />;
  if (!user) {
    const next = `${location.pathname}${location.search || ""}`;
    return <Navigate to={`/auth/login?next=${encodeURIComponent(next)}`} replace />;
  }

  return (
    <AccountLayout user={user} activeTab={tab} onNavigate={navigateTab} onLogout={confirmLogout} tier={data.vip?.current_tier?.name || null}>
      {loadError && <InlineNotice tone="warning">{loadError}</InlineNotice>}
      <div className={loadError ? "mt-6" : ""}>
        <AccountTabBoundary key={tab} tabKey={tab} t={t}>
          {tab === "overview" && <Overview user={user} account={data} loading={loading} onNavigate={navigateTab} />}
          {tab === "profile" && <PersonalInfo user={user} reloadUser={reload} />}
          {tab === "measurements" && <Measurements profile={data.profile} setProfile={(profile) => setData((current) => ({ ...current, profile }))} loading={loading} />}
          {tab === "addresses" && <Addresses addresses={data.addresses} setAddresses={(addresses) => setData((current) => ({ ...current, addresses }))} loading={loading} user={user} />}
          {tab === "orders" && <Orders orders={data.orders} loading={loading} />}
          {tab === "wardrobe" && <Wardrobe />}
          {tab === "tryon" && <TryOnHistory />}
          {tab === "avatar" && <StyleProfile />}
          {tab === "appointments" && <Appointments />}
          {tab === "concierge" && <ConciergeInbox user={user} />}
          {tab === "loyalty" && <LoyaltyVIP mode="summary" loyalty={data.loyalty} setLoyalty={(loyalty) => setData((current) => ({ ...current, loyalty }))} ledger={data.ledger} setLedger={(ledger) => setData((current) => ({ ...current, ledger }))} vip={data.vip} vouchers={data.vouchers} setVouchers={(vouchers) => setData((current) => ({ ...current, vouchers }))} />}
          {tab === "benefits" && <LoyaltyVIP mode="benefits" loyalty={data.loyalty} setLoyalty={(loyalty) => setData((current) => ({ ...current, loyalty }))} ledger={data.ledger} setLedger={(ledger) => setData((current) => ({ ...current, ledger }))} vip={data.vip} vouchers={data.vouchers} setVouchers={(vouchers) => setData((current) => ({ ...current, vouchers }))} />}
          {tab === "notifications" && <Notifications user={user} reloadUser={reload} />}
          {tab === "privacy" && <Privacy user={user} reloadUser={reload} onNavigate={navigateTab} />}
        </AccountTabBoundary>
      </div>
    </AccountLayout>
  );
}

function Overview({ user, account, loading, onNavigate }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [extras, setExtras] = useState({ appointments: [], tryon: [], wardrobe: null });
  const [extrasLoading, setExtrasLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setExtrasLoading(true);
    Promise.allSettled([
      api.get("/me/appointments"),
      api.get("/me/tryon/results"),
      api.get("/me/wardrobe"),
    ]).then(([appointments, tryon, wardrobe]) => {
      if (!active) return;
      setExtras({
        appointments: appointments.status === "fulfilled" ? appointments.value.data || [] : [],
        tryon: tryon.status === "fulfilled" ? tryon.value.data || [] : [],
        wardrobe: wardrobe.status === "fulfilled" ? wardrobe.value.data : null,
      });
      setExtrasLoading(false);
    });
    return () => { active = false; };
  }, []);

  const vip = account.vip || {};
  const loyalty = account.loyalty || {};
  const nt = vip.next_tier;
  const pct = vip.progress_percent ?? (nt ? 0 : 100);
  const completion = profileCompletion({ user, profile: account.profile, addresses: account.addresses });
  const latestOrder = safeArray(account.orders)[0];
  const appointment = nextAppointment(extras.appointments);
  const wardrobeCount = extras.wardrobe
    ? Object.values(extras.wardrobe).flatMap((items) => safeArray(items)).length
    : 0;
  const latestTryon = safeArray(extras.tryon)[0];

  if (loading || extrasLoading) {
    return <SkeletonRows count={5} />;
  }

  const heroName = user.first_name || fullName(user) || t("account.common.client");

  return (
    <div data-testid="account-overview" className="space-y-10">
      {/* Membership. The one thing a Falcon member opens this page to see, so it
          gets the whole width, real photography and a progress track you can
          actually read — not a 4px sliver under three equal-weight stat cards. */}
      <section className="relative overflow-hidden bg-graphite text-ivory">
        <div className="absolute inset-y-0 right-0 hidden w-[38%] lg:block">
          <AdfImage source={ss26Image(11)} sizes="38vw" className="h-full w-full object-cover opacity-[0.28]" />
          <div className="absolute inset-0 bg-gradient-to-r from-graphite via-graphite/70 to-transparent" />
        </div>
        <div className="relative grid gap-8 p-7 sm:p-9 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:p-11">
          <div className="min-w-0">
            <span className="adf-small text-ivory/58">{t("account.overview.hero.eyebrow")}</span>
            <h1 className="mt-4 font-serif text-[clamp(40px,5vw,68px)] leading-[0.95]">
              {t("account.overview.hero.title", "", { name: heroName })}
            </h1>

            <div className="mt-8 max-w-xl">
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-serif text-3xl leading-none">{vip.current_tier?.name || t("account.tier.member")}</span>
                <span className="adf-small text-ivory/58">{pct}%</span>
              </div>
              <div className="mt-3 h-[3px] w-full bg-ivory/15">
                <div className="h-full bg-gilt transition-all duration-700" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-3 text-sm text-ivory/68">
                {vip.lifetime_points ?? 0} {t("account.loyalty.lifetime_points")}
                {nt ? ` · ${t("account.overview.next_tier", "", { points: nt.points_to_go, tier: nt.name })}` : ` · ${t("account.overview.top_tier")}`}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-6 lg:items-end">
            <div className="flex gap-10">
              <div>
                <span className="adf-small block text-ivory/58">{t("account.overview.hero.points")}</span>
                <span className="mt-2 block font-serif text-4xl leading-none tabular-nums">{loyalty.balance ?? 0}</span>
              </div>
              <div>
                <span className="adf-small block text-ivory/58">{t("account.overview.hero.profile")}</span>
                <span className="mt-2 block font-serif text-4xl leading-none tabular-nums">{completion}%</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <PillCTA variant="onDark" onClick={() => onNavigate("orders")}>{t("account.action.view_orders")}</PillCTA>
              <PillCTA variant="secondary" onClick={() => onNavigate("benefits")} className="border-ivory/45 text-ivory hover:bg-ivory hover:text-graphite">
                {t("account.action.view_benefits")}
              </PillCTA>
            </div>
          </div>
        </div>
      </section>

      {/* Continue where you left off — two panels, not six tiles, and each one
          carries a picture because everything here is about garments. */}
      <section className="grid gap-6 lg:grid-cols-2">
        <FeaturePanel
          eyebrowKey="account.overview.module.order"
          actionKey="account.action.view_orders"
          onAction={() => onNavigate("orders")}
          image={latestOrder ? null : ss26Image(3)}
        >
          {latestOrder ? (
            <div>
              <div className="font-serif text-[34px] leading-none">#{latestOrder.display_id}</div>
              <p className="mt-3 text-sm text-graphite/70">
                {asDate(latestOrder.placed_at, locale)} · {formatAZN(latestOrder.total, locale)}
              </p>
              <div className="mt-4"><StatusPill status={latestOrder.status}>{labelFor(t, "status.order", latestOrder.status)}</StatusPill></div>
            </div>
          ) : (
            <p className="max-w-sm text-[15px] leading-7 text-graphite/70">{t("account.overview.empty.orders")}</p>
          )}
        </FeaturePanel>

        <FeaturePanel
          eyebrowKey="account.overview.module.appointment"
          actionKey="account.action.book_appointment"
          onAction={() => onNavigate("appointments")}
          image={appointment ? null : ss26Image(27)}
        >
          {appointment ? (
            <div>
              <div className="font-serif text-[34px] leading-none">{labelFor(t, "appointment.purpose", appointment.purpose)}</div>
              <p className="mt-3 text-sm text-graphite/70">{asDateTime(appointment.slot_start, locale)}</p>
              <div className="mt-4"><StatusPill status={appointment.status}>{labelFor(t, "appointment.status", appointment.status)}</StatusPill></div>
            </div>
          ) : (
            <p className="max-w-sm text-[15px] leading-7 text-graphite/70">{t("account.overview.empty.appointment")}</p>
          )}
        </FeaturePanel>
      </section>

      {/* Figures belong on one rule-separated line, not in three more boxes. */}
      <section className="grid grid-cols-2 gap-px border border-graphite/10 bg-graphite/10 md:grid-cols-3">
        <StatCell label={t("account.overview.wardrobe_count")} value={wardrobeCount} onClick={() => onNavigate("wardrobe")} />
        <StatCell label={t("account.overview.profile_complete")} value={`${completion}%`} onClick={() => onNavigate("profile")} />
        <StatCell
          label={t("account.overview.module.tryon")}
          value={safeArray(extras.tryon).length}
          onClick={() => onNavigate("tryon")}
          thumb={latestTryon ? mediaUrl(latestTryon.result_url) : null}
        />
      </section>

      <section>
        <span className="adf-small text-graphite/58">{t("account.overview.quick_actions")}</span>
        <div className="mt-4 flex flex-wrap gap-3">
          {[
            ["measurements", "account.action.complete_measurements"],
            ["wardrobe", "account.action.open_wardrobe"],
            ["concierge", "account.action.contact_atelier"],
          ].map(([key, labelKey]) => (
            <button
              key={key}
              type="button"
              onClick={() => onNavigate(key)}
              className="min-h-11 border border-graphite/18 px-5 text-sm text-graphite/75 transition hover:border-espresso hover:text-graphite focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso"
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

/** A wide panel with an optional photograph — used when there is something to
 *  show, and when there is not: an empty state that still looks like the house. */
function FeaturePanel({ eyebrowKey, actionKey, onAction, image, children }) {
  const { t } = useI18n();
  return (
    <article className="flex min-h-[248px] flex-col justify-between border border-graphite/10 bg-paper">
      {image && (
        <div className="h-32 w-full overflow-hidden">
          <AdfImage source={image} sizes="(min-width: 1024px) 50vw, 100vw" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="flex flex-1 flex-col justify-between p-6">
        <div>
          <span className="adf-small text-graphite/58">{t(eyebrowKey)}</span>
          <div className="mt-4">{children}</div>
        </div>
        <button
          type="button"
          onClick={onAction}
          className="mt-6 min-h-11 self-start border-b border-espresso pb-1 text-sm text-espresso transition hover:border-graphite hover:text-graphite focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso"
        >
          {t(actionKey)}
        </button>
      </div>
    </article>
  );
}

function StatCell({ label, value, onClick, thumb }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-4 bg-paper p-6 text-left transition hover:bg-stone/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso"
    >
      {thumb && <img src={thumb} alt="" loading="lazy" decoding="async" className="h-14 w-11 shrink-0 object-cover" />}
      <span className="min-w-0">
        <span className="block font-serif text-[32px] leading-none tabular-nums text-graphite">{value}</span>
        <span className="mt-2 block text-sm text-graphite/70">{label}</span>
      </span>
    </button>
  );
}

function OverviewModule({ icon, titleKey, actionKey, onAction, children }) {
  const { t } = useI18n();
  return (
    <article className="border border-graphite/10 bg-white p-5">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <IconBadge icon={icon} />
          <h2 className="text-sm font-medium text-graphite">{t(titleKey)}</h2>
        </div>
        {actionKey && (
          <button type="button" onClick={onAction} className="text-sm text-espresso underline-offset-4 hover:underline">
            {t(actionKey)}
          </button>
        )}
      </div>
      {children}
    </article>
  );
}

function CompactEmpty({ bodyKey }) {
  const { t } = useI18n();
  return <p className="text-sm leading-6 text-graphite/70">{t(bodyKey)}</p>;
}

function PersonalInfo({ user, reloadUser }) {
  const { t, locale, setLocale } = useI18n();
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    birth_date: user?.birth_date || "",
    marketing_consent: Boolean(user?.marketing_consent),
  });
  const [initial, setInitial] = useState(form);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [phoneOpen, setPhoneOpen] = useState(false);

  useEffect(() => {
    const next = {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      birth_date: user?.birth_date || "",
      marketing_consent: Boolean(user?.marketing_consent),
    };
    setForm(next);
    setInitial(next);
  }, [user]);

  const dirty = JSON.stringify({ ...form, email: initial.email, phone: initial.phone }) !== JSON.stringify(initial);
  useBeforeUnload(dirty);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setMessage("");
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const save = async () => {
    const nextErrors = {};
    if (!form.first_name.trim()) nextErrors.first_name = t("account.profile.error.first_name");
    if (!form.last_name.trim()) nextErrors.last_name = t("account.profile.error.last_name");
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const response = await api.put("/me/account", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        birth_date: form.birth_date || null,
        marketing_consent: form.marketing_consent,
      });
      await reloadUser?.();
      const next = {
        first_name: response.data.first_name || "",
        last_name: response.data.last_name || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        birth_date: response.data.birth_date || "",
        marketing_consent: Boolean(response.data.marketing_consent),
      };
      setForm(next);
      setInitial(next);
      setMessage(t("account.profile.saved"));
    } catch (error) {
      setMessage(error?.response?.data?.detail || t("account.profile.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrowKey="account.profile.eyebrow"
        titleKey="account.profile.title"
        bodyKey="account.profile.body"
        action={<PillCTA variant="primary" onClick={save} disabled={!dirty || saving}>{saving ? t("account.common.saving") : t("account.action.save_changes")}</PillCTA>}
      />
      <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="border border-graphite/10 bg-paper p-5">
          <div className="flex h-24 w-24 items-center justify-center bg-graphite font-serif text-3xl text-ivory" aria-hidden="true">
            {initialsFor(user)}
          </div>
          <h2 className="mt-4 font-serif text-2xl">{fullName(user) || t("account.common.client")}</h2>
          <p className="mt-2 text-sm leading-6 text-graphite/70">{t("account.profile.identity_note")}</p>
        </aside>
        <div className="space-y-6">
          {message && <InlineNotice tone={message === t("account.profile.saved") ? "success" : "warning"}>{message}</InlineNotice>}
          <div className="grid gap-5 md:grid-cols-2">
            <TextInput labelKey="account.profile.first_name" required value={form.first_name} onChange={(value) => update("first_name", value)} error={errors.first_name} testid="profile-first-name" />
            <TextInput labelKey="account.profile.last_name" required value={form.last_name} onChange={(value) => update("last_name", value)} error={errors.last_name} testid="profile-last-name" />
            <TextInput labelKey="account.profile.email" value={form.email} readOnly hintKey="account.profile.email_hint" />
            <div>
              <TextInput labelKey="account.profile.phone" value={form.phone} readOnly hintKey={user?.phone_verified ? "account.profile.phone_verified" : "account.profile.phone_unverified"} />
              <button type="button" onClick={() => setPhoneOpen(true)} className="mt-2 min-h-11 text-sm text-espresso underline-offset-4 hover:underline">
                {t("account.profile.change_phone")}
              </button>
            </div>
            <TextInput labelKey="account.profile.birth_date" type="date" value={form.birth_date} onChange={(value) => update("birth_date", value)} />
            <label className="block">
              <span className="text-sm font-medium text-graphite">{t("account.profile.language")}</span>
              <select
                value={locale}
                onChange={(event) => setLocale(event.target.value)}
                className="mt-2 min-h-11 w-full border border-graphite/18 bg-white px-3 text-sm text-graphite outline-none focus:border-espresso"
              >
                <option value="az">{t("account.profile.language_az")}</option>
                <option value="en">{t("account.profile.language_en")}</option>
              </select>
            </label>
          </div>
          <label className="flex min-h-11 items-start gap-3 border border-graphite/10 bg-paper p-4 text-sm leading-6 text-graphite/75">
            <input type="checkbox" checked={form.marketing_consent} onChange={(event) => update("marketing_consent", event.target.checked)} className="mt-1 accent-espresso" />
            <span>{t("account.profile.marketing_consent")}</span>
          </label>
        </div>
      </div>
      <PhoneChangePanel open={phoneOpen} onClose={() => setPhoneOpen(false)} onChanged={reloadUser} />
    </>
  );
}

function PhoneChangePanel({ open, onClose, onChanged }) {
  const { t } = useI18n();
  const [phoneDigits, setPhoneDigits] = useState("");
  const [code, setCode] = useState("");
  const [requested, setRequested] = useState(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const phone = e164Phone(phoneDigits);
  const phoneValid = Boolean(phone);
  const displayedPhone = displayAzPhone(phoneDigits);

  useEffect(() => {
    if (!open) {
      setPhoneDigits("");
      setCode("");
      setRequested(null);
      setBusy("");
      setMessage("");
    }
  }, [open]);

  const updatePhone = (value) => {
    setPhoneDigits(localPhoneDigits(value));
    setMessage("");
  };

  const request = async () => {
    if (!phoneValid) {
      setMessage(t("account.profile.phone_invalid"));
      return;
    }
    setBusy("request");
    setMessage("");
    try {
      const response = await api.post("/me/phone-change/request", { phone });
      setRequested({ ...response.data, phone: response.data?.phone || phone });
      setCode("");
      setMessage(response.data.debug_code ? t("account.profile.phone_debug", "", { code: response.data.debug_code }) : t("account.profile.phone_code_sent"));
    } catch (error) {
      setMessage(error?.response?.data?.detail || t("account.profile.phone_request_error"));
    } finally {
      setBusy("");
    }
  };

  const verify = async () => {
    const targetPhone = requested?.phone || phone;
    if (!targetPhone || code.length !== 6) return;
    setBusy("verify");
    setMessage("");
    try {
      await api.post("/me/phone-change/verify", { phone: targetPhone, code });
      await onChanged?.();
      setMessage(t("account.profile.phone_changed"));
      setTimeout(onClose, 600);
    } catch (error) {
      setMessage(error?.response?.data?.detail || t("account.profile.phone_verify_error"));
    } finally {
      setBusy("");
    }
  };

  return (
    <SidePanel open={open} titleKey="account.profile.phone_panel_title" onClose={onClose}>
      <div className="space-y-5">
        <p className="text-sm leading-6 text-graphite/75">{t("account.profile.phone_panel_body")}</p>
        <label className="block">
          <span className="text-sm font-medium text-graphite">{t("account.profile.new_phone")} <span className="text-red-900">*</span></span>
          {/* The shared control, not a fourth hand-rolled one: this was 48px
              tall with an "AZ" label while sign-in, checkout and recovery all
              used a 56px box with the flag. */}
          <div className={`adf-auth-phone-control ${phoneValid ? "is-valid" : ""} ${requested ? "opacity-75" : ""}`}>
            <div className="adf-auth-country" aria-hidden="true">
              <span>🇦🇿</span>
              <strong>{AZ_PHONE_PREFIX}</strong>
            </div>
            <input
              type="tel"
              inputMode="tel"
              placeholder="(50) 123 45 67"
              autoComplete="tel"
              value={formatLocalPhone(phoneDigits)}
              onChange={(event) => updatePhone(event.target.value)}
              onPaste={(event) => {
                event.preventDefault();
                updatePhone(event.clipboardData.getData("text"));
              }}
              placeholder="(50) 123 45 67"
              disabled={Boolean(requested) || Boolean(busy)}
              aria-invalid={phoneDigits.length > 0 && !phoneValid}
              aria-describedby="account-phone-change-help"
              className="min-w-0 bg-transparent text-[15px] text-graphite outline-none placeholder:text-graphite/70 disabled:cursor-not-allowed"
            />
          </div>
          <p id="account-phone-change-help" className={`mt-2 text-xs leading-5 ${phoneDigits.length > 0 && !phoneValid ? "text-red-900" : "text-graphite/70"}`}>
            {phoneDigits.length > 0 && !phoneValid ? t("account.profile.phone_invalid") : t("account.profile.new_phone_hint")}
          </p>
        </label>
        {requested && (
          <div className="border border-espresso/25 bg-stone/20 p-4">
            <p className="text-sm leading-6 text-graphite/75">
              {t("account.profile.phone_sent_to", "", { phone: requested.phone ? displayAzPhone(requested.phone) : displayedPhone })}
            </p>
            <button
              type="button"
              onClick={() => {
                setRequested(null);
                setCode("");
                setMessage("");
              }}
              className="mt-2 min-h-11 text-sm text-espresso underline-offset-4 hover:underline"
            >
              {t("account.profile.edit_phone")}
            </button>
          </div>
        )}
        {requested && (
          <TextInput
            labelKey="account.profile.otp_code"
            value={code}
            onChange={(value) => setCode(onlyOtpDigits(value))}
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
          />
        )}
        {message && <InlineNotice tone={message.includes("error") ? "danger" : "warning"}>{message}</InlineNotice>}
        <div className="flex flex-wrap gap-3">
          <PillCTA variant="primary" onClick={requested ? verify : request} disabled={busy || !phoneValid || (requested && code.length !== 6)}>
            {busy ? t("account.common.loading") : requested ? t("account.profile.verify_phone") : t("account.profile.send_code")}
          </PillCTA>
          {requested && <PillCTA variant="secondary" onClick={request} disabled={busy}>{t("account.profile.resend_code")}</PillCTA>}
        </div>
      </div>
    </SidePanel>
  );
}

function Measurements({ profile, setProfile, loading }) {
  const { t } = useI18n();
  const [form, setForm] = useState(normalizeProfile(profile));
  const [initial, setInitial] = useState(normalizeProfile(profile));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const next = normalizeProfile(profile);
    setForm(next);
    setInitial(next);
  }, [profile]);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  useBeforeUnload(dirty);
  const completion = profileCompletion({ user: {}, profile: form, addresses: [] });

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
    setMessage("");
  };

  const toggleArray = (key, value) => {
    setForm((current) => {
      const currentValues = safeArray(current[key]);
      const exists = currentValues.includes(value);
      return { ...current, [key]: exists ? currentValues.filter((item) => item !== value) : [...currentValues, value] };
    });
  };

  const validate = () => {
    const next = {};
    const height = Number(form.height_cm || 0);
    const weight = Number(form.weight_kg || 0);
    if (form.height_cm && (height < 120 || height > 230)) next.height_cm = t("account.measurements.error.height");
    if (form.weight_kg && (weight < 40 || weight > 180)) next.weight_kg = t("account.measurements.error.weight");
    if (form.shoe_size && (Number(form.shoe_size) < 36 || Number(form.shoe_size) > 48)) next.shoe_size = t("account.measurements.error.shoe");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage("");
    try {
      const payload = {
        ...form,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        favorite_colors: safeArray(form.favorite_colors),
        style_tags: safeArray(form.style_tags),
      };
      const response = await api.put("/me/profile", payload);
      const next = normalizeProfile(response.data);
      setForm(next);
      setInitial(next);
      setProfile(response.data);
      setMessage(t("account.measurements.saved"));
    } catch (error) {
      setMessage(error?.response?.data?.detail || t("account.measurements.save_error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !profile) return <SkeletonRows />;

  return (
    <>
      <PageHeader
        eyebrowKey="account.measurements.eyebrow"
        titleKey="account.measurements.title"
        bodyKey="account.measurements.body"
        action={<PillCTA variant="primary" onClick={save} disabled={!dirty || saving}>{saving ? t("account.common.saving") : t("account.action.save_changes")}</PillCTA>}
      />
      <div className="mb-7 border border-espresso/30 bg-stone/20 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <SmallCaps>{t("account.measurements.completion")}</SmallCaps>
            <p className="mt-2 text-sm leading-6 text-graphite/75">{t("account.measurements.use_note")}</p>
          </div>
          <div className="font-serif text-4xl">{completion}%</div>
        </div>
        <div className="mt-4 h-1 bg-graphite/10"><div className="h-full bg-espresso" style={{ width: `${completion}%` }} /></div>
      </div>
      {message && <div className="mb-5"><InlineNotice tone={message === t("account.measurements.saved") ? "success" : "warning"}>{message}</InlineNotice></div>}
      <div className="space-y-8">
        <FormSection titleKey="account.measurements.body_section">
          <div className="grid gap-5 md:grid-cols-3">
            <TextInput labelKey="account.measurements.height" hintKey="account.measurements.height_hint" value={form.height_cm} onChange={(value) => update("height_cm", value)} type="number" error={errors.height_cm} testid="m-height" />
            <TextInput labelKey="account.measurements.weight" hintKey="account.measurements.weight_hint" value={form.weight_kg} onChange={(value) => update("weight_kg", value)} type="number" error={errors.weight_kg} testid="m-weight" />
            <SelectInput labelKey="account.measurements.body_type" value={form.body_type} onChange={(value) => update("body_type", value)} options={["slim", "athletic", "regular", "broad"].map((value) => ({ value, labelKey: `account.body_type.${value}` }))} />
          </div>
        </FormSection>
        <FormSection titleKey="account.measurements.clothing_section">
          <div className="grid gap-5 md:grid-cols-4">
            <SelectInput labelKey="account.measurements.jacket" value={form.jacket_size} onChange={(value) => update("jacket_size", value)} options={["46", "48", "50", "52", "54", "56", "58"].map((value) => ({ value, labelKey: `account.size.${value}` }))} testid="m-jacket" />
            <SelectInput labelKey="account.measurements.trouser" value={form.trouser_size} onChange={(value) => update("trouser_size", value)} options={["44", "46", "48", "50", "52", "54", "56"].map((value) => ({ value, labelKey: `account.size.${value}` }))} testid="m-trouser" />
            <SelectInput labelKey="account.measurements.collar" value={form.shirt_collar_size} onChange={(value) => update("shirt_collar_size", value)} options={["38", "39", "40", "41", "42", "43", "44"].map((value) => ({ value, labelKey: `account.size.${value}` }))} testid="m-collar" />
            <TextInput labelKey="account.measurements.shoe" hintKey="account.measurements.shoe_hint" value={form.shoe_size} onChange={(value) => update("shoe_size", value)} type="number" error={errors.shoe_size} testid="m-shoe" />
          </div>
        </FormSection>
        <FormSection titleKey="account.measurements.style_section">
          <div className="space-y-6">
            <SegmentGroup labelKey="account.measurements.fit" value={form.preferred_fit} onChange={(value) => update("preferred_fit", value)} options={["slim", "classic", "relaxed"].map((value) => ({ value, labelKey: `account.fit.${value}` }))} />
            <TokenChooser labelKey="account.measurements.colors" values={safeArray(form.favorite_colors)} options={["midnight", "graphite", "ivory", "burgundy", "forest", "brass"]} namespace="color" onToggle={(value) => toggleArray("favorite_colors", value)} />
            <TokenChooser labelKey="account.measurements.styles" values={safeArray(form.style_tags)} options={["formal", "business", "evening", "weekend", "minimal", "classic"]} namespace="style_tag" onToggle={(value) => toggleArray("style_tags", value)} />
          </div>
        </FormSection>
      </div>
    </>
  );
}

function FormSection({ titleKey, children }) {
  const { t } = useI18n();
  return (
    <section>
      <h2 className="mb-4 font-serif text-3xl text-graphite">{t(titleKey)}</h2>
      <div className="border-t border-graphite/10 pt-5">{children}</div>
    </section>
  );
}

function TokenChooser({ labelKey, values, options, namespace, onToggle }) {
  const { t } = useI18n();
  return (
    <div>
      <div className="mb-2 text-sm font-medium text-graphite">{t(labelKey)}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onToggle(value)}
            className={`min-h-11 border px-4 text-sm transition ${
              values.includes(value) ? "border-graphite bg-graphite text-ivory" : "border-graphite/18 bg-white text-graphite/75 hover:border-espresso"
            }`}
          >
            {t(`account.${namespace}.${value}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

function Addresses({ addresses, setAddresses, loading, user }) {
  const { t } = useI18n();
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openCreate = () => {
    setEditing(null);
    setPanelOpen(true);
  };
  const openEdit = (address) => {
    setEditing(address);
    setPanelOpen(true);
  };
  const remove = async (address) => {
    if (!window.confirm(t("account.address.delete_confirm"))) return;
    await api.delete(`/me/addresses/${address.id}`);
    setAddresses(addresses.filter((item) => item.id !== address.id));
  };
  const makeDefault = async (address) => {
    const response = await api.patch(`/me/addresses/${address.id}`, { is_default_shipping: true });
    setAddresses(addresses.map((item) => item.id === address.id ? response.data : { ...item, is_default_shipping: false }));
  };

  if (loading) return <SkeletonRows />;

  return (
    <>
      <PageHeader
        eyebrowKey="account.address.eyebrow"
        titleKey="account.address.title"
        bodyKey="account.address.body"
        action={<PillCTA variant="primary" onClick={openCreate}>{t("account.address.add")}</PillCTA>}
      />
      {addresses.length === 0 ? (
        <EmptyState
          icon={MapPin}
          titleKey="account.address.empty_title"
          bodyKey="account.address.empty_body"
          action={<PillCTA variant="primary" onClick={openCreate}>{t("account.address.add")}</PillCTA>}
        />
      ) : (
        <div className="divide-y divide-graphite/10">
          {addresses.map((address) => (
            <article key={address.id} className="grid gap-4 py-5 md:grid-cols-[minmax(0,1fr)_auto]" data-testid={`addr-${address.id}`}>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-serif text-2xl">{address.label || t("account.address.label_fallback")}</h2>
                  {address.is_default_shipping && <StatusPill status="delivered">{t("account.address.default_shipping")}</StatusPill>}
                </div>
                <p className="mt-2 text-sm leading-6 text-graphite/75">
                  {[address.line1, address.line2, address.city, address.country].filter(Boolean).join(", ")}
                </p>
                <p className="mt-1 text-sm text-graphite/70">
                  {[address.first_name, address.last_name].filter(Boolean).join(" ")} · {address.phone || user?.phone || t("account.common.not_set")}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 md:justify-end">
                {!address.is_default_shipping && (
                  <button type="button" onClick={() => makeDefault(address)} className="min-h-11 text-sm text-espresso hover:underline">{t("account.address.make_default")}</button>
                )}
                <button type="button" onClick={() => openEdit(address)} className="min-h-11 text-sm text-graphite/75 hover:text-espresso">{t("account.common.edit")}</button>
                <button type="button" onClick={() => remove(address)} className="min-h-11 text-sm text-red-900/75 hover:text-red-900">{t("account.common.delete")}</button>
              </div>
            </article>
          ))}
        </div>
      )}
      <AddressPanel
        open={panelOpen}
        address={editing}
        user={user}
        onClose={() => setPanelOpen(false)}
        onSaved={(address) => {
          setAddresses(editing ? addresses.map((item) => item.id === address.id ? address : item) : [...addresses, address]);
          setPanelOpen(false);
        }}
      />
    </>
  );
}

function AddressPanel({ open, address, user, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ ...ADDRESS_DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm({
      ...ADDRESS_DEFAULTS,
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      phone: user?.phone || "",
      ...(address || {}),
    });
    setError("");
  }, [address, user, open]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    if (!form.first_name || !form.last_name || !form.phone || !form.city || !form.line1) {
      setError(t("account.address.required_error"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = address
        ? await api.patch(`/me/addresses/${address.id}`, form)
        : await api.post("/me/addresses", form);
      onSaved(response.data);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || t("account.address.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel open={open} titleKey={address ? "account.address.edit_title" : "account.address.new_title"} onClose={onClose}>
      <div className="space-y-5">
        <div className="grid gap-5 md:grid-cols-2">
          <TextInput labelKey="account.address.label" value={form.label} onChange={(value) => update("label", value)} />
          <TextInput labelKey="account.address.phone" value={form.phone} onChange={(value) => update("phone", value)} required />
          <TextInput labelKey="account.address.first_name" value={form.first_name} onChange={(value) => update("first_name", value)} required />
          <TextInput labelKey="account.address.last_name" value={form.last_name} onChange={(value) => update("last_name", value)} required />
          <TextInput labelKey="account.address.city" value={form.city} onChange={(value) => update("city", value)} required />
          <TextInput labelKey="account.address.postal" value={form.postal_code} onChange={(value) => update("postal_code", value)} />
        </div>
        <TextInput labelKey="account.address.line1" value={form.line1} onChange={(value) => update("line1", value)} required />
        <TextInput labelKey="account.address.line2" value={form.line2} onChange={(value) => update("line2", value)} />
        <label className="flex min-h-11 items-center gap-3 text-sm text-graphite/75">
          <input type="checkbox" checked={form.is_default_shipping} onChange={(event) => update("is_default_shipping", event.target.checked)} className="accent-espresso" />
          <span>{t("account.address.default_checkbox")}</span>
        </label>
        {error && <InlineNotice tone="danger">{error}</InlineNotice>}
        <PillCTA variant="primary" onClick={save} disabled={saving}>{saving ? t("account.common.saving") : t("account.action.save_changes")}</PillCTA>
      </div>
    </SidePanel>
  );
}

function Orders({ orders, loading }) {
  const { t, locale } = useI18n();
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const rows = useMemo(() => safeArray(orders).filter((order) => {
    if (filter === "active") return !["delivered", "cancelled", "returned"].includes(order.status);
    if (filter === "completed") return ["delivered"].includes(order.status);
    if (filter === "cancelled") return ["cancelled", "returned"].includes(order.status);
    return true;
  }), [filter, orders]);

  if (loading) return <SkeletonRows />;

  return (
    <>
      <PageHeader
        eyebrowKey="account.orders.eyebrow"
        titleKey="account.orders.title"
        bodyKey="account.orders.body"
        action={<PillCTA as={Link} to="/shop" variant="primary">{t("account.action.shop_collection")}</PillCTA>}
      />
      <FilterBar value={filter} onChange={setFilter} options={["all", "active", "completed", "cancelled"].map((value) => ({ value, labelKey: `account.orders.filter.${value}` }))} />
      {orders.length === 0 ? (
        <EmptyState
          icon={ShoppingBag}
          titleKey="account.orders.empty_title"
          bodyKey="account.orders.empty_body"
          action={<PillCTA as={Link} to="/shop" variant="primary">{t("account.action.shop_collection")}</PillCTA>}
        />
      ) : rows.length === 0 ? (
        <EmptyState icon={PackageCheck} titleKey="account.orders.no_filter_title" bodyKey="account.orders.no_filter_body" />
      ) : (
        <div className="mt-5 divide-y divide-graphite/10">
          {rows.map((order) => (
            <article key={order.id} className="grid gap-4 py-5 md:grid-cols-[minmax(0,1fr)_180px_auto]" data-testid={`order-${order.display_id}`}>
              <div className="min-w-0">
                <button type="button" onClick={() => setSelected(order)} className="text-left font-serif text-2xl text-graphite transition hover:text-espresso">
                  #{order.display_id}
                </button>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-graphite/70">
                  <span>{asDate(order.placed_at, locale)}</span>
                  <span aria-hidden="true">·</span>
                  <span>{safeArray(order.items).length} {t("account.orders.items")}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {safeArray(order.items).slice(0, 5).map((item, index) => (
                    <span key={item.id || index} className="h-14 w-11 shrink-0 overflow-hidden border border-graphite/10 bg-paper" title={pickLocale(item.title_snapshot, locale)}>
                      <AdfImage source={item.thumbnail_url || productImage(index)} sizes="44px" className="h-full w-full object-cover" />
                    </span>
                  ))}
                  {safeArray(order.items).length > 5 && (
                    <span className="flex h-14 w-11 items-center justify-center border border-graphite/10 bg-paper text-xs text-graphite/60">
                      +{safeArray(order.items).length - 5}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 md:items-start">
                {/* One customer-facing status. The fulfillment field is internal
                    and, at 'pending'/'not_fulfilled', translated to the same
                    "Hazırlanır" — showing both read as a duplicate. */}
                <StatusPill status={order.status}>{labelFor(t, "status.order", order.status)}</StatusPill>
              </div>
              <div className="flex flex-col items-start gap-3 md:items-end">
                <div className="font-serif text-2xl">{formatAZN(order.total, locale)}</div>
                <button type="button" onClick={() => setSelected(order)} className="min-h-11 text-sm text-espresso hover:underline">{t("account.orders.details")}</button>
              </div>
            </article>
          ))}
        </div>
      )}
      <OrderDetail order={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function FilterBar({ value, onChange, options }) {
  const { t } = useI18n();
  return (
    <div className="mb-5 flex gap-2 overflow-x-auto pb-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`min-h-11 flex-none border px-4 text-sm transition ${
            value === option.value ? "border-graphite bg-graphite text-ivory" : "border-graphite/18 bg-white text-graphite/75 hover:border-espresso"
          }`}
        >
          {t(option.labelKey, option.label || option.value)}
        </button>
      ))}
    </div>
  );
}

function OrderDetail({ order, onClose }) {
  const { t, locale } = useI18n();
  const [tracking, setTracking] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  useEffect(() => {
    if (!order?.id) return;
    let active = true;
    api.get(`/me/orders/${order.id}/tracking`)
      .then((response) => { if (active) setTracking(response.data); })
      .catch(() => { if (active) setTracking(null); });
    return () => { active = false; };
  }, [order?.id]);

  return (
    <SidePanel open={Boolean(order)} titleKey="account.orders.detail_title" onClose={onClose} width="max-w-2xl">
      {order && (
        <div className="space-y-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <SmallCaps>{t("account.orders.order_number")}</SmallCaps>
              <div className="mt-1 font-serif text-3xl">#{order.display_id}</div>
              <p className="mt-2 text-sm text-graphite/70">{asDateTime(order.placed_at, locale)}</p>
            </div>
            <StatusPill status={order.status}>{labelFor(t, "status.order", order.status)}</StatusPill>
          </div>
          <FormSection titleKey="account.orders.products">
            <div className="divide-y divide-graphite/10">
              {safeArray(order.items).map((item) => (
                <div key={item.id} className="grid grid-cols-[56px_1fr_auto] items-center gap-4 py-3">
                  <span className="h-[72px] w-14 shrink-0 overflow-hidden border border-graphite/10 bg-paper">
                    <AdfImage source={item.thumbnail_url || productImage(0)} sizes="56px" className="h-full w-full object-cover" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-serif text-lg leading-tight">{pickLocale(item.title_snapshot, locale) || item.sku}</div>
                    <p className="mt-1 text-xs uppercase tracking-[0.06em] text-graphite/55">
                      {item.variant_title ? `${item.variant_title} · ` : ""}SKU {item.sku}
                    </p>
                    <p className="mt-1 text-sm text-graphite/70">{item.quantity} × {formatAZN(item.unit_price, locale)}</p>
                  </div>
                  <div className="font-serif text-lg">{formatAZN(item.total, locale)}</div>
                </div>
              ))}
            </div>
          </FormSection>
          <FormSection titleKey="account.orders.price_breakdown">
            <div className="space-y-2 text-sm">
              <PriceRow labelKey="account.orders.subtotal" value={order.subtotal} />
              {/* Deductions carry their minus — "Endirim 1 ₼" read as a
                  1 ₼ surcharge next to lines that add. */}
              {(order.discount_total || 0) - (order.points_discount_minor || 0) > 0 && (
                <PriceRow labelKey="account.orders.discount" value={(order.discount_total || 0) - (order.points_discount_minor || 0)} negative />
              )}
              {(order.points_discount_minor || 0) > 0 && (
                <PriceRow labelKey="account.orders.points" value={order.points_discount_minor} negative />
              )}
              {(order.gift_card_total || 0) > 0 && (
                <PriceRow labelKey="account.orders.gift_card" value={order.gift_card_total} negative />
              )}
              <PriceRow labelKey="account.orders.shipping" value={order.shipping_total} />
              <PriceRow labelKey="account.orders.total" value={order.total} strong />
            </div>
          </FormSection>
          <FormSection titleKey="account.orders.shipping_address">
            <p className="text-sm leading-6 text-graphite/75">
              {[order.shipping_address?.line1, order.shipping_address?.line2, order.shipping_address?.city, order.shipping_address?.country].filter(Boolean).join(", ") || t("account.common.not_set")}
            </p>
          </FormSection>
          <FormSection titleKey="account.orders.timeline">
            {tracking?.timeline?.length ? (
              <div className="space-y-3">
                {tracking.timeline.map((item) => (
                  <div key={item.id} className="border-l border-espresso pl-4">
                    <div className="text-sm text-graphite">{labelFor(t, "status.order", item.to)}</div>
                    <div className="mt-1 text-xs text-graphite/70">{asDateTime(item.at, locale)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-graphite/70">{t("account.orders.timeline_empty")}</p>
            )}
            {tracking?.tracking_id && <p className="mt-4 text-sm text-graphite/75">{t("account.orders.tracking")}: {tracking.tracking_id}</p>}
          </FormSection>
          <div className="flex flex-wrap gap-3">
            <PillCTA variant="secondary" onClick={() => setInvoiceOpen(true)} data-testid="open-invoice">{t("account.orders.invoice")}</PillCTA>
            <PillCTA as={Link} to="/account?tab=concierge" variant="ghost">{t("account.action.contact_atelier")}</PillCTA>
          </div>
          <OrderInvoice order={order} tracking={tracking} open={invoiceOpen} onClose={() => setInvoiceOpen(false)} />
        </div>
      )}
    </SidePanel>
  );
}

function PriceRow({ labelKey, value, strong = false, negative = false }) {
  const { t, locale } = useI18n();
  return (
    <div className={`flex justify-between gap-4 ${strong ? "border-t border-graphite/10 pt-3 font-serif text-2xl" : ""}`}>
      <span>{t(labelKey)}</span>
      <span>{negative ? `− ${formatAZN(value, locale) || "0 ₼"}` : (formatAZN(value, locale) || "0 ₼")}</span>
    </div>
  );
}

function LoyaltyVIP({ mode, loyalty, setLoyalty, ledger, setLedger, vip, vouchers, setVouchers }) {
  const { t, locale } = useI18n();
  const [active, setActive] = useState(mode === "benefits" ? "benefits" : "overview");
  const [redeemPoints, setRedeemPoints] = useState(500);
  const [redeeming, setRedeeming] = useState(false);
  const [walletBusy, setWalletBusy] = useState(false);
  /* Whether Apple Wallet can actually issue a pass. The backend knows — it
     answers `{configured, available, missing}` — and the button used to be
     rendered regardless, so a member pressed it and got an error for a service
     that was never switched on. Undefined means "not asked yet"; the button
     waits rather than flashing in and out. */
  const [walletReady, setWalletReady] = useState(undefined);
  const [redeemMessage, setRedeemMessage] = useState("");
  const [walletMessage, setWalletMessage] = useState("");
  useEffect(() => {
    let active = true;
    api.get("/me/wallet/apple/status")
      .then((r) => { if (active) setWalletReady(Boolean(r.data?.available)); })
      /* Unreachable is not the same as unavailable, but for the member the
         outcome is identical: pressing it would fail. */
      .catch(() => { if (active) setWalletReady(false); });
    return () => { active = false; };
  }, []);

  const currentTier = vip?.current_tier;
  const nextTier = vip?.next_tier;
  const pct = vip?.progress_percent ?? (nextTier ? 0 : 100);
  const benefitRows = safeArray(vip?.benefits_status);

  const redeemValue = Math.floor(Number(redeemPoints || 0) / (loyalty?.voucher_points_per_azn || 20)) * 100;
  const redeem = async () => {
    setRedeemMessage("");
    if (redeemPoints <= 0 || redeemPoints % (loyalty?.voucher_points_per_azn || 20) !== 0) {
      setRedeemMessage(t("account.loyalty.redeem_multiple"));
      return;
    }
    if (redeemPoints > (loyalty?.balance || 0)) {
      setRedeemMessage(t("account.loyalty.redeem_too_many"));
      return;
    }
    if (!window.confirm(t("account.loyalty.redeem_confirm", "", { points: redeemPoints, value: formatAZN(redeemValue, locale) }))) return;
    setRedeeming(true);
    try {
      const result = await api.post("/me/loyalty/redeem", { points: redeemPoints });
      const [voucherResponse, ledgerResponse] = await Promise.all([api.get("/me/vouchers"), api.get("/me/loyalty/ledger")]);
      setVouchers(voucherResponse.data || []);
      setLedger(ledgerResponse.data || []);
      setLoyalty({ ...(loyalty || {}), balance: result.data.new_balance });
      setRedeemMessage(t("account.loyalty.redeem_success", "", { code: result.data.promotion_code, value: formatAZN(result.data.value_minor, locale) }));
    } catch (error) {
      setRedeemMessage(error?.response?.data?.detail || t("account.loyalty.redeem_error"));
    } finally {
      setRedeeming(false);
    }
  };

  const addToAppleWallet = async () => {
    setWalletMessage("");
    setWalletBusy(true);
    try {
      const response = await api.get("/me/wallet/apple/falcon.pkpass", {
        responseType: "blob",
        headers: { Accept: "application/vnd.apple.pkpass" },
      });
      const blob = new Blob([response.data], { type: "application/vnd.apple.pkpass" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "falcon-society.pkpass";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setWalletMessage(await apiErrorDetail(error, t("account.loyalty.apple_wallet_error")));
    } finally {
      setWalletBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrowKey="account.loyalty.eyebrow"
        titleKey={mode === "benefits" ? "account.benefits.title" : "account.loyalty.title"}
        bodyKey={mode === "benefits" ? "account.benefits.body" : "account.loyalty.body"}
      />
      <section className="mb-7 border border-espresso/30 bg-graphite p-6 text-ivory">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div>
            <SmallCaps tone="ivory" className="text-espresso">{t("account.loyalty.summary")}</SmallCaps>
            <h2 className="mt-3 font-serif text-[clamp(34px,4vw,54px)] leading-none">{currentTier?.name || t("account.tier.member")}</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <Metric labelKey="account.loyalty.current_points" value={loyalty?.balance ?? 0} dark />
              <Metric labelKey="account.loyalty.lifetime_points" value={vip?.lifetime_points ?? 0} dark />
              <Metric labelKey="account.loyalty.next_tier" value={nextTier?.name || t("account.overview.top_tier")} dark />
            </div>
          </div>
          <div className="min-w-[220px]">
            <div className="text-right font-serif text-4xl text-espresso">{pct}%</div>
            <div className="mt-3 h-1 bg-ivory/15"><div className="h-full bg-espresso" style={{ width: `${pct}%` }} /></div>
            <p className="mt-2 text-right text-xs text-ivory/68">
              {nextTier ? t("account.overview.next_tier", "", { points: nextTier.points_to_go, tier: nextTier.name }) : t("account.overview.top_tier")}
            </p>
            {walletReady !== false && (
              <PillCTA
                variant="onDark"
                onClick={addToAppleWallet}
                disabled={walletBusy || walletReady === undefined}
                className="mt-5 w-full"
              >
                {walletBusy ? t("account.common.loading") : t("account.loyalty.apple_wallet")}
              </PillCTA>
            )}
          </div>
        </div>
        {walletMessage && <div className="mt-5"><InlineNotice tone="warning">{walletMessage}</InlineNotice></div>}
      </section>
      <FilterBar value={active} onChange={setActive} options={["overview", "benefits", "ledger", "vouchers", "tiers"].map((value) => ({ value, labelKey: `account.loyalty.tab.${value}` }))} />
      <div className="mt-6">
        {active === "overview" && (
          <div className="grid gap-5 lg:grid-cols-2">
            {benefitRows.slice(0, 4).map((benefit) => <BenefitRow key={benefit.key} benefit={benefit} />)}
            {benefitRows.length === 0 && <EmptyState icon={Sparkles} titleKey="account.loyalty.empty_benefits_title" bodyKey="account.loyalty.empty_benefits_body" />}
          </div>
        )}
        {active === "benefits" && (
          <div className="grid gap-4 lg:grid-cols-2">
            {benefitRows.map((benefit) => <BenefitRow key={benefit.key} benefit={benefit} />)}
          </div>
        )}
        {active === "ledger" && (
          <div className="divide-y divide-graphite/10">
            {safeArray(ledger).map((entry) => (
              <div key={entry.id} className="grid gap-2 py-4 sm:grid-cols-[1fr_130px_90px] sm:items-center">
                <div className="font-serif text-xl">{entry.reason}</div>
                <div className="text-sm text-graphite/70">{asDate(entry.created_at, locale)}</div>
                <div className={`text-left font-serif text-xl sm:text-right ${entry.delta > 0 ? "text-espresso" : "text-graphite"}`}>{entry.delta > 0 ? "+" : ""}{entry.delta}</div>
              </div>
            ))}
            {!safeArray(ledger).length && <EmptyState icon={WalletCards} titleKey="account.loyalty.empty_ledger_title" bodyKey="account.loyalty.empty_ledger_body" />}
          </div>
        )}
        {active === "vouchers" && (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
            <div className="border border-graphite/10 bg-paper p-5">
              <SmallCaps>{t("account.loyalty.create_voucher")}</SmallCaps>
              <p className="mt-3 text-sm leading-6 text-graphite/75">{t("account.loyalty.voucher_rule", "", { rate: loyalty?.voucher_points_per_azn || 20 })}</p>
              <div className="mt-5">
                <TextInput labelKey="account.loyalty.points_to_convert" value={redeemPoints} onChange={(value) => setRedeemPoints(Number(value || 0))} type="number" />
                <p className="mt-2 text-sm text-graphite/70">{t("account.loyalty.voucher_value")}: {formatAZN(redeemValue, locale)}</p>
              </div>
              {redeemMessage && <div className="mt-4"><InlineNotice tone="warning">{redeemMessage}</InlineNotice></div>}
              <PillCTA variant="primary" onClick={redeem} disabled={redeeming || redeemPoints > (loyalty?.balance || 0)} className="mt-5">
                {redeeming ? t("account.common.loading") : t("account.loyalty.create_voucher")}
              </PillCTA>
            </div>
            <div className="divide-y divide-graphite/10">
              {safeArray(vouchers).map((voucher) => (
                <div key={voucher.id} className="flex items-center justify-between gap-4 py-4">
                  <div>
                    <div className="font-serif text-2xl">{voucher.promotion_code || t("account.loyalty.voucher")}</div>
                    <p className="mt-1 text-sm text-graphite/70">{formatAZN(voucher.value_minor, locale)} · {labelFor(t, "voucher.status", voucher.status)}</p>
                  </div>
                  {voucher.promotion_code && <button type="button" onClick={() => navigator.clipboard?.writeText(voucher.promotion_code)} className="min-h-11 text-sm text-espresso hover:underline">{t("account.loyalty.copy_code")}</button>}
                </div>
              ))}
              {!safeArray(vouchers).length && <EmptyState icon={WalletCards} titleKey="account.loyalty.empty_vouchers_title" bodyKey="account.loyalty.empty_vouchers_body" />}
            </div>
          </div>
        )}
        {active === "tiers" && (
          <div className="space-y-3">
            {safeArray(vip?.all_tiers).map((tier) => (
              <div key={tier.code} className={`grid gap-3 border-l-2 p-4 md:grid-cols-[1fr_auto] ${tier.code === currentTier?.code ? "border-espresso bg-stone/25" : "border-graphite/12 bg-white"}`}>
                <div>
                  <div className="font-serif text-2xl">{tier.name}</div>
                  <p className="mt-1 text-sm text-graphite/70">
                    {tier.invite_only ? t("account.loyalty.invite_only") : t("account.loyalty.tier_points", "", { points: tier.min_lifetime_points })}
                  </p>
                </div>
                <StatusPill status={tier.code === currentTier?.code ? "delivered" : "pending"}>
                  {tier.code === currentTier?.code ? t("account.loyalty.current_tier") : tier.invite_only ? t("account.loyalty.private_tier") : t("account.loyalty.locked_tier")}
                </StatusPill>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function Metric({ labelKey, value, dark = false }) {
  const { t } = useI18n();
  return (
    <div>
      <div className={`text-xs ${dark ? "text-ivory/58" : "text-graphite/70"}`}>{t(labelKey)}</div>
      <div className={`mt-1 font-sans text-2xl font-medium tabular-nums ${dark ? "text-ivory" : "text-graphite"}`}>{value}</div>
    </div>
  );
}

function BenefitRow({ benefit }) {
  const { t } = useI18n();
  return (
    <article className={`border p-4 ${benefit.unlocked ? "border-espresso/35 bg-stone/20" : "border-graphite/10 bg-white"}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl">{benefit.label}</h3>
          <p className="mt-2 text-sm leading-6 text-graphite/70">
            {benefit.unlocked ? t("account.loyalty.benefit_available") : t("account.loyalty.benefit_locked")}
          </p>
        </div>
        <StatusPill status={benefit.unlocked ? "delivered" : "pending"}>
          {benefit.unlocked ? t("account.common.available") : t("account.common.locked")}
        </StatusPill>
      </div>
    </article>
  );
}

function Wardrobe() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { addItem } = useCart() || {};
  const [data, setData] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [looks, setLooks] = useState([]);
  const [active, setActive] = useState("saved");
  const [sort, setSort] = useState("newest");
  const [lookName, setLookName] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [wardrobeResponse, suggestionsResponse, looksResponse] = await Promise.all([
        api.get("/me/wardrobe"),
        api.get("/me/wardrobe/suggestions"),
        api.get("/me/looks"),
      ]);
      setData(wardrobeResponse.data);
      setSuggestions(suggestionsResponse.data);
      setLooks(looksResponse.data || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || t("account.wardrobe.load_error"));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { value: "saved", labelKey: "account.wardrobe.tab.saved" },
    { value: "looks", labelKey: "account.wardrobe.tab.looks" },
    { value: "tried", labelKey: "account.wardrobe.tab.tried" },
    { value: "reserved", labelKey: "account.wardrobe.tab.reserved" },
  ];
  const savedItems = ["suit", "shirt", "shoes", "accessory"].flatMap((key) => safeArray(data?.[key]).map((item) => ({ ...item, sectionKey: key })));
  const triedItems = safeArray(data?.ai_tried).map((item) => ({ ...item, sectionKey: "ai_tried" }));
  const reservedItems = safeArray(data?.reserved).map((item) => ({ ...item, sectionKey: "reserved" }));
  const currentItems = active === "reserved" ? reservedItems : active === "tried" ? triedItems : savedItems;
  const sortedItems = [...currentItems].sort((a, b) => {
    if (sort === "price") return Number(a.product?.min_price || 0) - Number(b.product?.min_price || 0);
    return new Date(b.added_at || b.expires_at || 0) - new Date(a.added_at || a.expires_at || 0);
  });

  const remove = async (item) => {
    if (!window.confirm(t("account.wardrobe.remove_confirm"))) return;
    if (item.sectionKey === "reserved") await api.delete(`/me/reservations/${item.id}`);
    else await api.delete(`/me/wardrobe/${item.id}`);
    await load();
  };

  // Client-side navigation: a full page load here threw away the cart context
  // that the line above had just updated, so the basket arrived looking empty
  // until its own fetch came back.
  const addToBag = async (item) => {
    if (!item.variant_id && !item.variant?.id) {
      navigate(`/products/${item.product.handle}`);
      return;
    }
    await addItem?.(item.variant_id || item.variant.id, 1);
    navigate("/cart");
  };

  const saveLookFromSuggestions = async () => {
    if (!suggestions?.anchor) return;
    const items = [{ product_id: suggestions.anchor.id, slot: "suit" }];
    Object.entries(suggestions.suggestions || {}).forEach(([slot, rows]) => {
      if (rows?.[0]) items.push({ product_id: rows[0].id, slot });
    });
    await api.post("/me/looks", { name: lookName || t("account.wardrobe.default_look_name"), items });
    setLookName("");
    await load();
  };

  if (!data && !error) return <SkeletonRows count={5} />;

  return (
    <>
      <PageHeader
        eyebrowKey="account.wardrobe.eyebrow"
        titleKey="account.wardrobe.title"
        bodyKey="account.wardrobe.body"
        action={<PillCTA as={Link} to="/shop" variant="primary">{t("account.action.discover_products")}</PillCTA>}
      />
      {error && <div className="mb-5"><InlineNotice tone="danger">{error}</InlineNotice></div>}
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <FilterBar value={active} onChange={setActive} options={tabs} />
        {active !== "looks" && (
          <select value={sort} onChange={(event) => setSort(event.target.value)} className="min-h-11 border border-graphite/18 bg-white px-3 text-sm outline-none focus:border-espresso">
            <option value="newest">{t("account.wardrobe.sort.newest")}</option>
            <option value="price">{t("account.wardrobe.sort.price")}</option>
          </select>
        )}
      </div>
      {active === "looks" ? (
        <SavedLooks looks={looks} suggestions={suggestions} lookName={lookName} setLookName={setLookName} onSave={saveLookFromSuggestions} />
      ) : sortedItems.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          titleKey="account.wardrobe.empty_title"
          bodyKey="account.wardrobe.empty_body"
          action={<PillCTA as={Link} to="/shop" variant="primary">{t("account.action.discover_products")}</PillCTA>}
          secondary={<PillCTA as={Link} to="/shop" variant="secondary">{t("account.action.start_tryon")}</PillCTA>}
        />
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 xl:grid-cols-4">
          {sortedItems.map((item, index) => (
            <article key={item.id} data-testid={`wardrobe-item-${item.sectionKey}-${item.product?.handle || item.id}`} className="group">
              <Link to={`/products/${item.product.handle}`} className="block aspect-[3/4] overflow-hidden bg-paper">
                <AdfImage source={item.product.thumbnail_url || productImage(index)} sizes="(min-width: 1280px) 20vw, (min-width: 768px) 30vw, 45vw" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
              </Link>
              <div className="mt-3">
                <div className="text-xs text-espresso">{sectionTitle(t, item.sectionKey)}</div>
                <h2 className="mt-1 font-serif text-xl leading-tight">{compactProductTitle(item.product, locale)}</h2>
                <p className="mt-1 text-sm text-graphite/70">{formatAZN(item.product.min_price, locale)} · {item.variant?.title || item.variant_id || t("account.common.size_later")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.product.ai_try_on_enabled && <Link to={`/products/${item.product.handle}`} className="min-h-11 text-sm text-espresso hover:underline">{t("account.action.tryon")}</Link>}
                  <button type="button" onClick={() => addToBag(item)} className="min-h-11 text-sm text-graphite/75 hover:text-espresso">{t("account.action.add_to_bag")}</button>
                  <button type="button" onClick={() => remove(item)} className="min-h-11 text-sm text-red-900/70 hover:text-red-900">{item.sectionKey === "reserved" ? t("account.wardrobe.release") : t("account.common.remove")}</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

function SavedLooks({ looks, suggestions, lookName, setLookName, onSave }) {
  const { t, locale } = useI18n();
  return (
    <div className="space-y-8">
      {suggestions?.anchor && (
        <section className="border border-espresso/25 bg-stone/15 p-5">
          <SmallCaps>{t("account.wardrobe.curated")}</SmallCaps>
          <h2 className="mt-2 font-serif text-3xl">{compactProductTitle(suggestions.anchor, locale)}</h2>
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-5">
            {Object.entries(suggestions.suggestions || {}).map(([slot, items], index) => (
              <div key={slot}>
                <div className="mb-2 text-xs text-graphite/70">{slot}</div>
                {items?.[0] ? (
                  <Link to={`/products/${items[0].handle}`} className="block">
                    <div className="aspect-[3/4] overflow-hidden bg-paper">
                      <AdfImage source={items[0].thumbnail_url || productImage(index + 4)} sizes="(min-width: 768px) 18vw, 45vw" className="h-full w-full object-cover" />
                    </div>
                    <div className="mt-2 text-sm">{compactProductTitle(items[0], locale)}</div>
                  </Link>
                ) : <div className="flex aspect-[3/4] items-center justify-center bg-paper text-graphite/70">-</div>}
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
            <TextInput labelKey="account.wardrobe.look_name" value={lookName} onChange={setLookName} />
            <PillCTA variant="primary" onClick={onSave}>{t("account.wardrobe.save_look")}</PillCTA>
          </div>
        </section>
      )}
      {safeArray(looks).length === 0 ? (
        <EmptyState icon={Sparkles} titleKey="account.wardrobe.empty_looks_title" bodyKey="account.wardrobe.empty_looks_body" />
      ) : (
        <div className="divide-y divide-graphite/10">
          {looks.map((look) => (
            <article key={look.id} className="py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-serif text-2xl">{look.name}</h2>
                  <p className="mt-1 text-sm text-graphite/70">{asDate(look.created_at, locale)} · {safeArray(look.items).length} {t("account.wardrobe.pieces")}</p>
                </div>
                <StatusPill status="confirmed">{t("account.wardrobe.look")}</StatusPill>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                {safeArray(look.items).map((item, index) => (
                  <Link key={`${look.id}-${item.slot}-${index}`} to={`/products/${item.product.handle}`} className="block">
                    <div className="aspect-[3/4] overflow-hidden bg-paper">
                      <AdfImage source={item.product.thumbnail_url || productImage(index)} sizes="(min-width: 768px) 22vw, 45vw" className="h-full w-full object-cover" />
                    </div>
                    <div className="mt-2 text-sm">{compactProductTitle(item.product, locale)}</div>
                  </Link>
                ))}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function TryOnHistory() {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/me/tryon/results");
      setRows(response.data || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || t("account.tryon.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((row) => {
    if (filter === "recent") return new Date(row.created_at).getTime() > Date.now() - 1000 * 60 * 60 * 24 * 14;
    if (filter === "looks") return String(row.provider || "").includes("look");
    if (filter === "pieces") return !String(row.provider || "").includes("look");
    return true;
  });
  const remove = async (row) => {
    if (!window.confirm(t("account.tryon.delete_confirm"))) return;
    await api.delete(`/me/tryon/${row.id}`);
    setRows((current) => current.filter((item) => item.id !== row.id));
  };
  const save = async (row) => {
    try {
      await api.post("/me/tryon/save-to-wardrobe", { result_id: row.id });
      setMessage(t("account.tryon.saved"));
    } catch (requestError) {
      setMessage(requestError?.response?.data?.detail || t("account.tryon.save_error"));
    }
  };
  const share = async (row) => {
    const url = mediaUrl(row.result_url);
    if (navigator.share) await navigator.share({ title: t("account.tryon.share_title"), url });
    else {
      await navigator.clipboard?.writeText(url);
      setMessage(t("account.tryon.copied"));
    }
  };

  if (loading) return <SkeletonRows />;

  return (
    <>
      <PageHeader
        eyebrowKey="account.tryon.eyebrow"
        titleKey="account.tryon.title"
        bodyKey="account.tryon.body"
        action={<PillCTA as={Link} to="/shop" variant="primary">{t("account.action.first_tryon")}</PillCTA>}
      />
      {error && <div className="mb-5"><InlineNotice tone="danger">{error}</InlineNotice></div>}
      {message && <div className="mb-5"><InlineNotice>{message}</InlineNotice></div>}
      <FilterBar value={filter} onChange={setFilter} options={["all", "recent", "looks", "pieces"].map((value) => ({ value, labelKey: `account.tryon.filter.${value}` }))} />
      {rows.length === 0 ? (
        <EmptyState
          icon={ImageIcon}
          titleKey="account.tryon.empty_title"
          bodyKey="account.tryon.empty_body"
          action={<PillCTA as={Link} to="/shop" variant="primary">{t("account.action.first_tryon")}</PillCTA>}
        />
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 xl:grid-cols-4">
          {filtered.map((row) => (
            <article key={row.id}>
              <Link to={row.product_handle ? `/products/${row.product_handle}` : "/shop"} className="block aspect-[3/4] overflow-hidden bg-paper">
                <img loading="lazy" decoding="async" src={mediaUrl(row.result_url)} alt="" className="h-full w-full object-cover"
                     onError={(event) => {
                       if (event.currentTarget.dataset.refreshed) return;
                       event.currentTarget.dataset.refreshed = "1";
                       void load();
                     }} />
              </Link>
              <h2 className="mt-3 font-serif text-xl leading-tight">{pickLocale(row.product_title, locale) || t("account.tryon.preview")}</h2>
              <p className="mt-1 text-sm text-graphite/70">{labelFor(t, "fit", row.fit)} · {asDate(row.created_at, locale)}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <IconAction icon={Download} labelKey="account.tryon.download" onClick={() => window.open(mediaUrl(row.result_url), "_blank", "noopener,noreferrer")} />
                <IconAction icon={Share2} labelKey="account.tryon.share" onClick={() => share(row)} />
                <IconAction icon={Sparkles} labelKey="account.tryon.save" onClick={() => save(row)} />
                <IconAction icon={Trash2} labelKey="account.common.delete" danger onClick={() => remove(row)} />
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

function IconAction({ icon: Icon, labelKey, onClick, danger = false }) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex min-h-11 items-center gap-1.5 text-sm ${danger ? "text-red-900/75 hover:text-red-900" : "text-graphite/70 hover:text-espresso"}`}
    >
      <Icon size={14} strokeWidth={1.5} /> {t(labelKey)}
    </button>
  );
}

// The try-on avatar is a body stand-in: a customer who would rather not upload
// a photo can still try garments on a figure that matches their build. So this
// screen is deliberately just two paths — upload a photo, or pick a body avatar
// — and nothing else. Fit / colours / style tags live on the Measurements tab;
// silhouette and accent never reached Try-On and are gone. There is no separate
// "generate" step: the shipped photoreal library answers instantly and free, so
// choosing a body/skin/hair and pressing "use this avatar" is the whole flow.
const AVATAR_AXES = [
  { field: "body_shape", optionsKey: "body_shapes" },
  { field: "complexion", optionsKey: "complexions" },
  { field: "hair", optionsKey: "hairs" },
];

function StyleProfile() {
  const { t } = useI18n();
  const fileRef = useRef(null);
  const [options, setOptions] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [params, setParams] = useState({ body_shape: "regular", complexion: "ivory", hair: "swept" });
  const [mode, setMode] = useState("avatar");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState(false);
  const [checks, setChecks] = useState([]);
  // A failed preview used to be patched by writing straight to img.src, which
  // React never undoes — the frame then kept showing the fallback portrait even
  // after the selection changed. Held in state so the next src wins.
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    api.get("/avatar/options").then((response) => setOptions(response.data)).catch(() => {});
    api.get("/me/avatar").then((response) => {
      setAvatar(response.data);
      if (response.data?.params) setParams({
        body_shape: response.data.params.body_shape || "regular",
        complexion: response.data.params.complexion || "ivory",
        hair: response.data.params.hair || "swept",
      });
      if (response.data?.source === "uploaded_photo") setMode("photo");
      else if (response.data?.source === "ai_generated") setMode("avatar");
      // "No avatar" is a 200 with a null id, not a 404 — the endpoint always
      // answers so it can carry `requirements`. Treating any 200 as "has one"
      // would disable the prefill below for exactly the customers it exists for.
      return Boolean(response.data?.id);
    }).catch(() => {
      // The endpoint answers 200 with a null id when there is no avatar, so a
      // rejection here is a real failure. It used to be swallowed entirely,
      // which left the screen offering to create an avatar the customer may
      // already have.
      notify(t("account.style.load_error", "Avatarınızı yükləmək alınmadı. Səhifəni yeniləyin."), true);
      return false;
    }).then((hasAvatar) => {
      // The customer already told us their build on the Measurements tab;
      // asking again with a "regular" default was the only place the two could
      // disagree. Chained rather than fired in parallel so a saved avatar
      // always wins — prefill is for the first one only.
      if (hasAvatar) return;
      api.get("/me/profile").then((response) => {
        const bodyType = response.data?.body_type;
        if (bodyType) setParams((current) => ({ ...current, body_shape: bodyType }));
      }).catch(() => {});
    });
    // Mount only. `t` is read for one error message; listing it would re-run the
    // whole avatar lookup and re-prefill the build every time the shopper
    // switches language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A new selection deserves a fresh attempt; otherwise one transient 502 would
  // pin the frame to the fallback for the rest of the session.
  useEffect(() => { setPreviewFailed(false); }, [params.body_shape, params.complexion, params.hair, avatar?.image_url]);

  const notify = (text, isError = false) => { setMessage(text); setMessageError(isError); };

  const photoAvatar = avatar?.source === "uploaded_photo";
  const bodyAvatar = avatar?.source === "ai_generated";
  // Avatars from the old parametric-SVG era still have a row and still render
  // here, but Try-On refuses them (routers/tryon.py only accepts
  // uploaded_photo / ai_generated). The customer saw a healthy avatar and an
  // error telling them to create one they thought they already had.
  const staleAvatar = Boolean(avatar?.id) && !photoAvatar && !bodyAvatar;
  const requirements = avatar?.requirements || options?.photo_requirements || {};
  // Live library portrait for the current selection — free, never persisted, so
  // changing the build visibly changes the person before anything is saved.
  const livePreview = `${API_BASE}/avatar/preview?${new URLSearchParams(params).toString()}`;
  const savedMatches = bodyAvatar
    && avatar?.params?.body_shape === params.body_shape
    && avatar?.params?.complexion === params.complexion
    && avatar?.params?.hair === params.hair;
  const avatarPreviewSrc = previewFailed
    ? defaultAiAvatar
    : (savedMatches ? mediaUrl(avatar.image_url) : livePreview);

  const selectBodyAvatar = async () => {
    setSaving(true);
    setMessage("");
    try {
      const response = await api.post("/me/avatar", params);
      setAvatar(response.data);
      notify(t("account.style.avatar_saved_msg"));
    } catch (error) {
      notify(error?.response?.data?.detail || t("account.style.ai_error"), true);
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSaving(true);
    setMessage("");
    setChecks([]);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const response = await api.post("/me/avatar/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(response.data);
      setMode("photo");
      setChecks(response.data?.params?.checks || []);
      notify(t("account.style.photo_saved"));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      notify(typeof detail === "string" ? detail : detail?.message || t("account.style.photo_error"), true);
      setChecks(detail?.checks || []);
    } finally {
      setSaving(false);
      if (event.target) event.target.value = "";
    }
  };

  const removeAvatar = async () => {
    if (!window.confirm(t("account.style.delete_confirm"))) return;
    await api.delete("/me/avatar");
    setAvatar(null);
    setChecks([]);
    notify("");
  };

  const forkTabs = [
    { value: "photo", labelKey: "account.style.tab.photo" },
    { value: "avatar", labelKey: "account.style.tab.ai" },
  ];

  return (
    <>
      <PageHeader eyebrowKey="account.style.eyebrow" titleKey="account.style.title" bodyKey="account.style.body" />
      {message && <div className="mb-5"><InlineNotice tone={messageError ? "warning" : "success"}>{message}</InlineNotice></div>}

      {/* The real fork: upload a photo, or use a body-matched avatar. */}
      <div role="tablist" aria-label={t("account.style.title")} className="mb-7 grid grid-cols-2 gap-2 sm:inline-grid sm:auto-cols-max sm:grid-flow-col">
        {forkTabs.map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={mode === item.value}
            onClick={() => setMode(item.value)}
            className={`min-h-11 border px-5 text-sm transition ${mode === item.value ? "border-graphite bg-graphite text-ivory" : "border-graphite/20 bg-white text-graphite/75 hover:border-espresso"}`}
          >
            {t(item.labelKey)}
          </button>
        ))}
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Preview */}
        <section>
          <div className="overflow-hidden border border-graphite/10 bg-white">
            {/* 2:3, because that is what the library actually ships: every tile
                is 1024x1536. The frame used to be 4/5 with object-cover, which
                scaled each portrait up until it filled the box and cropped 20%
                off a head-to-shoes figure — and cropped different amounts for
                different tiles, so changing the hair visibly resized the man. */}
            <div className="aspect-[2/3] bg-paper">
              {mode === "photo" ? (
                photoAvatar ? (
                  <img loading="lazy" decoding="async" src={mediaUrl(avatar.image_url)} alt={t("account.style.avatar_preview_alt")} className="h-full w-full object-contain" />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center p-8 text-center text-graphite/70">
                    <ImageIcon size={34} strokeWidth={1.3} className="text-espresso" />
                    <h2 className="mt-5 font-serif text-2xl text-graphite sm:text-3xl">{t("account.style.no_photo_title")}</h2>
                    <p className="mt-2 max-w-xs text-sm leading-6">{t("account.style.no_photo_body")}</p>
                  </div>
                )
              ) : (
                <img
                  key={avatarPreviewSrc}
                  loading="lazy"
                  decoding="async"
                  src={avatarPreviewSrc}
                  onError={() => setPreviewFailed(true)}
                  alt={t("account.style.ai_preview_alt")}
                  className="h-full w-full object-contain"
                />
              )}
            </div>
          </div>
          {staleAvatar && (
            <p role="status" data-testid="avatar-stale-notice" className="mt-3 border border-espresso/25 bg-paper px-4 py-3 text-[13px] leading-5 text-graphite/80">
              {t("account.style.stale_avatar", "Bu avatar köhnə formatdadır və Try-On-da işləmir. Aşağıdan yenisini seçin və ya foto yükləyin.")}
            </p>
          )}
          {mode === "avatar" && (
            <p className="mt-3 text-xs leading-5 text-graphite/60">{t("account.style.avatar_note")}</p>
          )}
          {mode === "photo" && <AvatarChecks checks={checks.length ? checks : safeArray(avatar?.params?.checks)} />}
        </section>

        {/* Controls */}
        <section className="space-y-6">
          {mode === "photo" && (
            <div className="border border-graphite/10 bg-white p-5 sm:p-6">
              <SmallCaps>{t("account.style.photo_best")}</SmallCaps>
              <h2 className="mt-3 font-serif text-2xl text-graphite sm:text-3xl">{t("account.style.photo_title")}</h2>
              <p className="mt-2 text-sm leading-6 text-graphite/70">{t("account.style.photo_body")}</p>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} />
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <PillCTA variant="primary" className="w-full justify-center sm:w-auto" onClick={() => fileRef.current?.click()} disabled={saving}>
                  <Upload size={15} /> {photoAvatar ? t("account.style.replace_photo") : t("account.style.upload_photo")}
                </PillCTA>
                {photoAvatar && <PillCTA variant="secondary" className="w-full justify-center sm:w-auto" onClick={removeAvatar} disabled={saving}>{t("account.style.delete_avatar")}</PillCTA>}
              </div>
              <div className="mt-6 space-y-3 border-t border-graphite/10 pt-5">
                {AVATAR_REQUIREMENT_KEYS.map((key) => (
                  <div key={key} className="flex gap-3 text-sm leading-6 text-graphite/75">
                    <CheckCircle2 size={15} strokeWidth={1.5} className="mt-1 flex-none text-espresso" />
                    <span>{t(key)}</span>
                  </div>
                ))}
                <p className="pt-1 text-xs text-graphite/60">
                  JPG · PNG · WEBP · {requirements.max_upload_mb || 15} MB · {requirements.min_short_edge_px || 640}px+
                </p>
              </div>
            </div>
          )}

          {mode === "avatar" && (
            <div className="border border-graphite/10 bg-white p-5 sm:p-6">
              <h2 className="font-serif text-2xl text-graphite sm:text-3xl">{t("account.style.avatar_intro_title")}</h2>
              <p className="mt-2 text-sm leading-6 text-graphite/70">{t("account.style.avatar_intro_body")}</p>
              <div className="mt-6 space-y-5">
                {AVATAR_AXES.map(({ field, optionsKey }) => (
                  <div key={field}>
                    <div className="mb-2 text-sm font-medium text-graphite">{t(`account.avatar.${field}`)}</div>
                    <div className="flex flex-wrap gap-2">
                      {((options && options[optionsKey]) || []).map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setParams((current) => ({ ...current, [field]: value }))}
                          className={`min-h-11 border px-4 text-sm transition ${params[field] === value ? "border-graphite bg-graphite text-ivory" : "border-graphite/18 bg-white text-graphite/75 hover:border-espresso"}`}
                        >
                          {t(`account.avatar.option.${value}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-col gap-3 border-t border-graphite/10 pt-5 sm:flex-row sm:flex-wrap sm:items-center">
                {savedMatches ? (
                  <span className="inline-flex min-h-11 items-center gap-2 text-sm text-espresso">
                    <CheckCircle2 size={16} strokeWidth={1.6} /> {t("account.style.avatar_selected")}
                  </span>
                ) : (
                  <PillCTA variant="primary" className="w-full justify-center sm:w-auto" onClick={selectBodyAvatar} disabled={saving || !options}>
                    <Sparkles size={15} /> {saving ? t("account.common.saving") : (bodyAvatar ? t("account.style.avatar_update") : t("account.style.avatar_select"))}
                  </PillCTA>
                )}
                {bodyAvatar && <PillCTA variant="secondary" className="w-full justify-center sm:w-auto" onClick={removeAvatar} disabled={saving}>{t("account.style.delete_avatar")}</PillCTA>}
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function AvatarChecks({ checks = [] }) {
  const { t } = useI18n();
  if (!checks.length) return null;
  return (
    <div className="mt-5 space-y-2">
      {checks.map((check) => {
        const labelKey = `account.style.check.${check.key}.label`;
        const messageKey = `account.style.check.${check.key}.${check.status}`;
        return (
          <div key={check.key} className={`flex gap-2 border p-2 text-xs leading-5 ${check.status === "pass" ? "border-emerald-900/15 bg-emerald-50 text-emerald-950" : check.status === "warning" ? "border-espresso/35 bg-stone/35 text-graphite" : "border-red-900/20 bg-red-50 text-red-900"}`}>
            <span className="mt-0.5">{check.status === "pass" ? <Check size={14} /> : <AlertCircle size={14} />}</span>
            <span><strong>{t(labelKey, check.label)}:</strong> {t(messageKey, check.message)}</span>
          </div>
        );
      })}
    </div>
  );
}

function ConciergeInbox({ user }) {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/me/concierge/requests");
      const list = response.data || [];
      setRows(list);
      setSelectedId((current) => current || list[0]?.id || null);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || t("account.concierge.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);
  const selected = rows.find((row) => row.id === selectedId) || rows[0];

  const reply = async () => {
    if (!selected || !draft.trim()) return;
    setBusy("reply");
    try {
      await api.post(`/me/concierge/requests/${selected.id}/messages`, { body: draft.trim() });
      setDraft("");
      await load();
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || t("account.concierge.reply_error"));
    } finally {
      setBusy("");
    }
  };

  if (loading) return <SkeletonRows count={4} />;

  return (
    <>
      <PageHeader
        eyebrowKey="account.concierge.eyebrow"
        titleKey="account.concierge.title"
        bodyKey="account.concierge.body"
        action={<PillCTA variant="primary" onClick={() => setNewOpen(true)}>{t("account.concierge.new")}</PillCTA>}
      />
      {error && <div className="mb-5"><InlineNotice tone="danger">{error}</InlineNotice></div>}
      {rows.length === 0 ? (
        <EmptyState
          icon={MessageSquareText}
          titleKey="account.concierge.empty_title"
          bodyKey="account.concierge.empty_body"
          action={<PillCTA variant="primary" onClick={() => setNewOpen(true)}>{t("account.concierge.new")}</PillCTA>}
        />
      ) : (
        <div className="grid min-h-[560px] gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="border border-graphite/10 bg-paper">
            {rows.map((request) => (
              <button
                key={request.id}
                type="button"
                onClick={() => setSelectedId(request.id)}
                className={`block w-full border-b border-graphite/10 p-4 text-left transition ${selected?.id === request.id ? "bg-stone/35" : "hover:bg-white"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-serif text-xl">{request.product ? compactProductTitle(request.product, locale) : t("account.concierge.private_request")}</span>
                  {request.priority && <Lock size={14} className="text-espresso" />}
                </div>
                <p className="mt-2 line-clamp-2 text-sm leading-5 text-graphite/70">{request.message}</p>
                <p className="mt-2 text-xs text-graphite/70">{asDate(request.created_at, locale)} · {labelFor(t, "concierge.status", request.status)}</p>
              </button>
            ))}
          </aside>
          <section className="border border-graphite/10 bg-white p-5">
            {selected && (
              <div className="flex h-full flex-col">
                <div className="border-b border-graphite/10 pb-4">
                  <SmallCaps>{labelFor(t, "concierge.status", selected.status)}</SmallCaps>
                  <h2 className="mt-2 font-serif text-3xl">{selected.product ? compactProductTitle(selected.product, locale) : t("account.concierge.private_request")}</h2>
                  <p className="mt-2 text-sm text-graphite/70">{asDateTime(selected.created_at, locale)}</p>
                </div>
                <div className="flex-1 space-y-3 overflow-y-auto py-5">
                  {safeArray(selected.messages).length === 0 && (
                    <div className="border border-espresso/25 bg-stone/15 p-4 text-sm leading-6 text-graphite/75">{selected.message}</div>
                  )}
                  {safeArray(selected.messages).map((message) => {
                    const mine = message.author_user_id === user.id;
                    return (
                      <div key={message.id} className={`max-w-[85%] p-4 ${mine ? "ml-auto bg-graphite text-ivory" : "border border-espresso/25 bg-stone/15 text-graphite"}`}>
                        <div className="text-xs opacity-55">{mine ? t("account.common.you") : t("account.common.atelier")}</div>
                        <p className="mt-2 text-sm leading-6">{message.body}</p>
                        <div className="mt-2 text-xs opacity-45">{asDateTime(message.created_at, locale)}</div>
                      </div>
                    );
                  })}
                </div>
                {selected.status !== "closed" && (
                  <div className="border-t border-graphite/10 pt-4">
                    <TextAreaInput labelKey="account.concierge.reply" value={draft} onChange={setDraft} rows={3} />
                    <PillCTA variant="secondary" className="mt-3" onClick={reply} disabled={busy === "reply" || !draft.trim()}>{busy ? t("account.common.loading") : t("account.concierge.send")}</PillCTA>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}
      <NewConciergePanel open={newOpen} onClose={() => setNewOpen(false)} onCreated={async () => { setNewOpen(false); await load(); }} user={user} />
    </>
  );
}

function NewConciergePanel({ open, onClose, onCreated, user }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ service: "styling", subject: "", message: "", product_handle: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async () => {
    if (!form.message.trim()) {
      setError(t("account.concierge.message_required"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/concierge/requests", {
        name: fullName(user),
        email: user.email,
        phone: user.phone,
        product_handle: form.product_handle || null,
        message: [form.subject, form.message].filter(Boolean).join("\n\n"),
        source: `account_${form.service}`,
      });
      analytics.lead(`account_${form.service}`, form.product_handle ? { handle: form.product_handle } : null);
      setForm({ service: "styling", subject: "", message: "", product_handle: "" });
      onCreated();
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || t("account.concierge.create_error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <SidePanel open={open} titleKey="account.concierge.new_title" onClose={onClose}>
      <div className="space-y-5">
        <SelectInput labelKey="account.concierge.service" value={form.service} onChange={(value) => update("service", value)} options={["styling", "order", "alteration", "falcon"].map((value) => ({ value, labelKey: `account.concierge.service.${value}` }))} required />
        <TextInput labelKey="account.concierge.subject" value={form.subject} onChange={(value) => update("subject", value)} />
        <TextInput labelKey="account.concierge.product_handle" hintKey="account.concierge.product_hint" value={form.product_handle} onChange={(value) => update("product_handle", value)} />
        <TextAreaInput labelKey="account.concierge.message" value={form.message} onChange={(value) => update("message", value)} rows={6} />
        {error && <InlineNotice tone="danger">{error}</InlineNotice>}
        <PillCTA variant="primary" onClick={submit} disabled={busy}>{busy ? t("account.common.loading") : t("account.concierge.create")}</PillCTA>
      </div>
    </SidePanel>
  );
}

function Appointments() {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("upcoming");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/me/appointments");
      setRows(response.data || []);
    } catch (error) {
      console.error("[atelier] load appointments failed", error);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const cancel = async (appointment) => {
    if (!window.confirm(t("account.appointments.cancel_confirm"))) return;
    await api.delete(`/me/appointments/${appointment.id}`);
    await load();
  };
  const now = Date.now();
  const filtered = rows.filter((row) => {
    const upcoming = new Date(row.slot_start).getTime() >= now && !["cancelled", "completed"].includes(row.status);
    return active === "upcoming" ? upcoming : !upcoming;
  });
  if (loading) return <SkeletonRows />;
  return (
    <>
      <PageHeader
        eyebrowKey="account.appointments.eyebrow"
        titleKey="account.appointments.title"
        bodyKey="account.appointments.body"
        action={<PillCTA variant="primary" onClick={() => setOpen(true)} data-testid="apt-open-modal">{t("account.appointments.new")}</PillCTA>}
      />
      <AppointmentModal open={open} onClose={() => { setOpen(false); load(); }} />
      <FilterBar value={active} onChange={setActive} options={["upcoming", "past"].map((value) => ({ value, labelKey: `account.appointments.tab.${value}` }))} />
      {filtered.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          titleKey={active === "upcoming" ? "account.appointments.empty_upcoming_title" : "account.appointments.empty_past_title"}
          bodyKey={active === "upcoming" ? "account.appointments.empty_upcoming_body" : "account.appointments.empty_past_body"}
          action={active === "upcoming" ? <PillCTA variant="primary" onClick={() => setOpen(true)}>{t("account.appointments.new")}</PillCTA> : null}
        />
      ) : (
        <div className="mt-5 divide-y divide-graphite/10">
          {filtered.map((appointment) => (
            <article key={appointment.id} className="grid gap-4 py-5 md:grid-cols-[1fr_220px_auto] md:items-center" data-testid={`apt-row-${appointment.id}`}>
              <div>
                <h2 className="font-serif text-2xl">{labelFor(t, "appointment.purpose", appointment.purpose)}</h2>
                <p className="mt-1 text-sm text-graphite/70">{appointment.notes || t("account.appointments.default_note")}</p>
              </div>
              <div>
                <div className="text-sm text-graphite">{asDateTime(appointment.slot_start, locale)}</div>
                <div className="mt-1 text-xs text-graphite/70">{appointment.stylist ? `${appointment.stylist.first_name || ""} ${appointment.stylist.last_name || ""}`.trim() : t("account.appointments.assigning")}</div>
              </div>
              <div className="flex flex-wrap gap-2 md:justify-end">
                <StatusPill status={appointment.status}>{labelFor(t, "appointment.status", appointment.status)}</StatusPill>
                {active === "upcoming" && <button type="button" onClick={() => cancel(appointment)} className="min-h-11 text-sm text-red-900/75 hover:text-red-900">{t("account.common.cancel")}</button>}
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

function Notifications({ user, reloadUser }) {
  const { t, locale } = useI18n();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const [updatingConsent, setUpdatingConsent] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/me/notifications");
      setData(response.data || { items: [], unread: 0 });
    } catch (error) {
      console.error("[atelier] load notifications failed", error);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const markRead = async (id) => { await api.post(`/me/notifications/${id}/read`); load(); };
  const markAllRead = async () => { if (data.unread > 0) { await api.post("/me/notifications/read-all"); load(); } };
  const updateMarketing = async (value) => {
    setUpdatingConsent(true);
    try {
      await api.put("/me/account", { marketing_consent: value });
      await reloadUser?.();
    } finally {
      setUpdatingConsent(false);
    }
  };
  const groups = groupNotifications(data.items);
  if (loading) return <SkeletonRows />;
  return (
    <>
      <PageHeader
        eyebrowKey="account.notifications.eyebrow"
        titleKey="account.notifications.title"
        bodyKey="account.notifications.body"
        action={<PillCTA variant="secondary" onClick={markAllRead} disabled={!data.unread}>{t("account.notifications.mark_all")}</PillCTA>}
      />
      {data.items.length === 0 ? (
        <EmptyState icon={Bell} titleKey="account.notifications.empty_title" bodyKey="account.notifications.empty_body" />
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <section key={group.key}>
              <SmallCaps>{t(group.labelKey)}</SmallCaps>
              <div className="mt-3 divide-y divide-graphite/10">
                {group.items.map((item) => (
                  <article key={item.id} className={`grid gap-4 py-4 md:grid-cols-[1fr_130px_auto] ${!item.read_at ? "bg-stone/12 px-3" : ""}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        {!item.read_at && <span className="h-2 w-2 bg-espresso" aria-hidden="true" />}
                        <h2 className="font-serif text-xl">{pickLocale(item.title, locale) || labelFor(t, "notification.type", item.type)}</h2>
                      </div>
                      <p className="mt-1 text-sm leading-6 text-graphite/70">{pickLocale(item.body, locale)}</p>
                      <p className="mt-1 text-xs text-graphite/70">{labelFor(t, "notification.type", item.type)}</p>
                    </div>
                    <div className="text-sm text-graphite/70">{asDate(item.created_at, locale)}</div>
                    {!item.read_at && <button type="button" onClick={() => markRead(item.id)} className="min-h-11 text-sm text-espresso hover:underline">{t("account.notifications.mark_read")}</button>}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      <section className="mt-10 border-t border-graphite/10 pt-6">
        <h2 className="font-serif text-3xl">{t("account.notifications.settings")}</h2>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <SettingRow labelKey="account.notifications.channel.inapp" enabled readOnly />
          <SettingRow labelKey="account.notifications.channel.email" enabled={Boolean(user.email)} readOnly />
          <SettingRow labelKey="account.notifications.channel.push" enabled={false} readOnly />
          <SettingRow labelKey="account.notifications.channel.marketing" enabled={Boolean(user.marketing_consent)} onChange={updateMarketing} disabled={updatingConsent} />
        </div>
      </section>
    </>
  );
}

function groupNotifications(items) {
  const now = Date.now();
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
  const groups = [
    { key: "today", labelKey: "account.notifications.group.today", items: [] },
    { key: "week", labelKey: "account.notifications.group.week", items: [] },
    { key: "older", labelKey: "account.notifications.group.older", items: [] },
  ];
  safeArray(items).forEach((item) => {
    const ts = new Date(item.created_at).getTime();
    if (ts >= todayStart.getTime()) groups[0].items.push(item);
    else if (ts >= weekAgo) groups[1].items.push(item);
    else groups[2].items.push(item);
  });
  return groups.filter((group) => group.items.length);
}

function SettingRow({ labelKey, enabled, readOnly = false, disabled = false, onChange }) {
  const { t } = useI18n();
  return (
    <label className={`flex min-h-14 items-center justify-between gap-4 border border-graphite/10 bg-white px-4 ${readOnly ? "text-graphite/70" : "text-graphite"}`}>
      <span className="text-sm">{t(labelKey)}</span>
      <input type="checkbox" checked={enabled} disabled={readOnly || disabled} onChange={(event) => onChange?.(event.target.checked)} className="accent-espresso" />
    </label>
  );
}

function Privacy({ user, reloadUser, onNavigate }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");

  const deleteTryOn = async () => {
    if (!window.confirm(t("account.privacy.delete_tryon_confirm"))) return;
    setBusy("tryon");
    setMessage("");
    try {
      const response = await api.delete("/me/tryon");
      setMessage(t("account.privacy.delete_tryon_success", "", { count: response.data.deleted || 0 }));
    } catch (error) {
      setMessage(error?.response?.data?.detail || t("account.privacy.delete_tryon_error"));
    } finally {
      setBusy("");
    }
  };

  const exportData = async () => {
    setBusy("export");
    setMessage("");
    try {
      const response = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `atelier-difalco-data-${new Date().toISOString().slice(0, 10)}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
      setMessage(t("account.privacy.export_success"));
    } catch (error) {
      setMessage(error?.response?.data?.detail || t("account.privacy.export_error"));
    } finally {
      setBusy("");
    }
  };

  const updateMarketing = async (value) => {
    setBusy("consent");
    setMessage("");
    try {
      await api.put("/me/account", { marketing_consent: value });
      await reloadUser?.();
      setMessage(t("account.privacy.consent_saved"));
    } catch (error) {
      setMessage(error?.response?.data?.detail || t("account.privacy.consent_error"));
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <PageHeader eyebrowKey="account.privacy.eyebrow" titleKey="account.privacy.title" bodyKey="account.privacy.body" />
      {message && <div className="mb-5"><InlineNotice tone="warning">{message}</InlineNotice></div>}
      <div className="space-y-9">
        <FormSection titleKey="account.privacy.consents">
          <div className="grid gap-3 md:grid-cols-2">
            <SettingRow labelKey="account.privacy.marketing_emails" enabled={Boolean(user.marketing_consent)} onChange={updateMarketing} disabled={busy === "consent"} />
            <SettingRow labelKey="account.privacy.personal_offers" enabled={Boolean(user.marketing_consent)} onChange={updateMarketing} disabled={busy === "consent"} />
          </div>
        </FormSection>
        <FormSection titleKey="account.privacy.tryon_data">
          <p className="text-sm leading-6 text-graphite/75">{t("account.privacy.tryon_body")}</p>
          <PillCTA variant="secondary" className="mt-4" onClick={deleteTryOn} disabled={busy === "tryon"}>{busy === "tryon" ? t("account.common.loading") : t("account.privacy.delete_tryon")}</PillCTA>
        </FormSection>
        <FormSection titleKey="account.privacy.export">
          <p className="text-sm leading-6 text-graphite/75">{t("account.privacy.export_body")}</p>
          <PillCTA variant="secondary" className="mt-4" onClick={exportData} disabled={busy === "export"}>{busy === "export" ? t("account.common.loading") : t("account.privacy.export_action")}</PillCTA>
        </FormSection>
        <section className="border-t border-red-900/20 pt-6">
          <h2 className="font-serif text-3xl text-red-950">{t("account.privacy.danger_title")}</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-graphite/75">{t("account.privacy.delete_account_body")}</p>
          <PillCTA variant="ghost" className="mt-4 text-red-900 hover:text-red-700" onClick={() => onNavigate("concierge")}>
            {t("account.privacy.delete_account_request")}
          </PillCTA>
        </section>
      </div>
    </>
  );
}
